import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { useAuth } from "../stores/auth";
import { Td, Th, errMessage } from "../components/eex/ui";
import { fmtEur0, fmtEur2, fmtPx, fmtQty } from "../lib/eex-format";
import type {
  BankRecon,
  CurrencyDisplayContext,
  ContractView,
  ContractViewKind,
  ExcludedTrade,
  FxBasis,
  GroupDimension,
  GroupedNode,
  GroupedTreeResponse,
  LeafExplanation,
  LeafRequest,
  Lifecycle,
  MatchMethod,
  PositionMode,
  PriceDisplayMode,
  PositionsTreeRequest,
  ProvenanceBucket,
  SideAgg,
  TradeOrigin,
  UnmappedProduct,
  ValuationIncompleteReason,
  VmWindowSummary,
} from "../features/eex/positions-tree/contracts";
import {
  buildPositionsTreeRequestPath,
  positionsTreeQueryKeys,
  resolveVmWindowRequest,
  type VmWindowPreset,
} from "../features/eex/positions-tree/queries";
import {
  selectLeafRequestFromNode,
  selectPositionPrice,
  selectPositionMonthFacts,
  selectPositionVmFacts,
  type PositionPriceFacts,
  type PositionPriceField,
  type PositionMonthFacts,
} from "../features/eex/positions-tree/selectors";

export const Route = createFileRoute("/eex/positions-tree")({
  component: EexPositionsTreePage,
});

const METHOD_LABELS: Record<MatchMethod, string> = {
  fifo: "FIFO",
  lifo: "LIFO",
  weighted_average: "Weighted average",
  specific_lot: "Specific lot",
};

const DIM_LABELS: Record<GroupDimension, string> = {
  book: "Book",
  account: "Account (P1/P2)",
  pair: "Pair",
  deliveryPeriod: "Delivery",
  product: "Product",
  trader: "Trader",
  venue: "Venue",
};

const ALL_DIMENSIONS: GroupDimension[] = [
  "book",
  "account",
  "pair",
  "deliveryPeriod",
  "product",
  "trader",
  "venue",
];

const DEFAULT_GROUPING: GroupDimension[] = ["book", "deliveryPeriod", "product"];

// YYYY-MM-DD from a local Date (the <input type="date"> value format).
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Sensible default delivery window for the interval view, computed at runtime:
// first day of the previous month → last day of two months ahead. Round month
// boundaries so the picker lands on clean dates.
// Europe/Berlin calendar "today" — the exchange/clearing day the VM presets end on.
function berlinToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function defaultDeliveryWindow(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 3, 0); // day 0 of (now+3) = last day of (now+2)
  return { from: isoDate(from), to: isoDate(to) };
}

// The cascade is a real, irreversible exchange event — there is ONE position set
// (cascade-applied = what the bank holds). The old as-traded toggle was retired;
// we always request cascaded. The backend always cascades regardless, but its
// contract still takes a `mode`, so we send this constant.
const MODE: PositionMode = "cascaded";

// ── Metric columns (data-driven, reorderable) ─────────────────────────────────
// The tree's value columns. The leading "Group" column is the tree itself
// (indentation + chevron + labels) and is pinned; only these metric columns are
// drag-reorderable and their order is persisted per user (see TreePrefs). Each
// column's `render` owns its full <Td> (classes are node-conditional), so the
// header and every row iterate the SAME ordered list — one source of truth.
type MetricColId =
  | "netMW"
  | "buyMW"
  | "sellMW"
  | "avgBuy"
  | "avgSell"
  | "netPx"
  | "settle"
  | "realized"
  | "mtm"
  | "total"
  | "vm";

interface MetricCol {
  id: string; // MetricColId for the persisted catalog; "m:YYYY-MM" for month columns
  label: string;
  render: (node: GroupedNode, priceDisplay: PriceDisplayContext) => ReactNode;
  // Month columns are window-generated: fixed in place, not draggable/persisted.
  static?: boolean;
}

interface PriceDisplayContext {
  mode: PriceDisplayMode;
  fxBasis: FxBasis;
  currency: CurrencyDisplayContext;
}

const EMPTY_CURRENCY_DISPLAY: CurrencyDisplayContext = {
  eurRonSpot: null,
  eurRonForwards: {},
};

function priceReferenceLabel(
  rate: number | null,
  date: string | null,
  source: string | null
): string {
  if (rate == null) return "unavailable";
  return `EUR/RON ${fmtPx(rate, 4)}${date ? ` · ${date}` : ""}${source ? ` · ${source}` : ""}`;
}

function positionPriceTooltip(
  field: PositionPriceField,
  facts: PositionPriceFacts
): string {
  const priceLabel = field === "settle" ? "Settlement" : "Transaction price";
  if (facts.transactionCurrency === "GBP") {
    return `${priceLabel}: ${facts.nativeValue != null ? fmtPx(facts.nativeValue, 2) : "—"} GBP/MWh\nEUR conversion: unavailable — no EUR/GBP display reference is configured`;
  }
  if (facts.transactionCurrency === "EUR") {
    return `${priceLabel}: ${facts.value != null ? fmtPx(facts.value, 2) : "—"} EUR/MWh`;
  }
  const deliveryBasisLabel = facts.forwardKinds.length > 0 && facts.forwardKinds.every((kind) => kind === "actual")
    ? "actual delivery FX"
    : facts.forwardKinds.some((kind) => kind !== "forward") ? "delivery-period FX" : "forward FX";
  const months = facts.forwardMonths;
  const scope = facts.forwardAggregation === "delivery_weighted" && months.length > 0
    ? ` · delivery-period equivalent (${months[0]}${months.length > 1 ? ` to ${months[months.length - 1]}` : ""})`
    : months.length === 1 ? ` · ${months[0]}` : "";
  const forwardReference = field === "settle" && facts.nativeAtForward != null && facts.forwardRate == null
    ? `Converted by delivery period; no single equivalent rate for a zero EUR mark${scope}`
    : `${priceReferenceLabel(facts.forwardRate, facts.forwardDate, facts.forwardSource)}${scope}`;
  const basisNote = `Selected FX basis: ${facts.fxBasis === "spot" ? "Spot" : "Forward"} (prices and bilateral position estimates)`;
  const missingNote = facts.forwardMissingMonths.length > 0
    ? `Missing delivery FX: ${facts.forwardMissingMonths.join(", ")}` : null;
  if (field === "settle") {
    return [
      `Settlement: ${facts.eurValue != null ? fmtPx(facts.eurValue, 2) : "—"} EUR/MWh`,
      `Native at current FX: ${facts.nativeAtSpot != null ? `${fmtPx(facts.nativeAtSpot, 2)} RON/MWh` : "unavailable"} (${priceReferenceLabel(facts.spotRate, facts.spotDate, facts.spotSource)})`,
      `Native at ${deliveryBasisLabel}: ${facts.nativeAtForward != null ? `${fmtPx(facts.nativeAtForward, 2)} RON/MWh` : "unavailable"} (${forwardReference})`,
      missingNote,
      basisNote,
    ].filter(Boolean).join("\n");
  }
  if (facts.nativeValue == null) {
    return `${priceLabel}: unavailable\n${basisNote}`;
  }
  return [
    `Native price: ${fmtPx(facts.nativeValue, 2)} RON/MWh`,
    `EUR at current FX: ${facts.eurAtSpot != null ? `${fmtPx(facts.eurAtSpot, 2)} EUR/MWh` : "unavailable"} (${priceReferenceLabel(facts.spotRate, facts.spotDate, facts.spotSource)})`,
    `EUR at ${deliveryBasisLabel}: ${facts.eurAtForward != null ? `${fmtPx(facts.eurAtForward, 2)} EUR/MWh` : "unavailable"} (${forwardReference})`,
    missingNote,
    basisNote,
  ].filter(Boolean).join("\n");
}

function renderPositionPrice(
  node: GroupedNode,
  field: PositionPriceField,
  priceDisplay: PriceDisplayContext,
  extraClass?: string
): ReactNode {
  const facts = selectPositionPrice(node, field, priceDisplay.mode, priceDisplay.currency, priceDisplay.fxBasis);
  const detail = positionPriceTooltip(field, facts);
  const title = [
    detail,
    field === "settle" && node.mtmPriceDate ? `Settlement date: ${node.mtmPriceDate}` : null,
    field === "netPrice"
      ? "Net price is the cost basis of the open MW left after matching."
      : null,
  ].filter(Boolean).join("\n");
  return (
    <span
      className={cn(facts.native && "cursor-help italic", extraClass)}
      title={title}
    >
      {facts.value != null ? fmtPx(facts.value, 2) : "—"}
      {facts.native && <span className="ml-1 text-[9px] uppercase opacity-70">{facts.currency}</span>}
    </span>
  );
}

const METRIC_COLUMNS: MetricCol[] = [
  {
    id: "netMW",
    label: "Net MW",
    render: (node) => (
      <Td
        className={cn(
          "text-right tabular-nums font-semibold",
          node.netMW > 0 ? "text-posGreen" : node.netMW < 0 ? "text-negRed" : "text-muted"
        )}
      >
        {node.netMW > 0 ? `+${node.netMW}` : node.netMW}
      </Td>
    ),
  },
  {
    id: "buyMW",
    label: "Buy MW",
    render: (node) => <Td className="text-right tabular-nums">{node.grossLongMW || ""}</Td>,
  },
  {
    id: "sellMW",
    label: "Sell MW",
    render: (node) => (
      <Td className="text-right tabular-nums">{node.grossShortMW ? -node.grossShortMW : ""}</Td>
    ),
  },
  {
    id: "avgBuy",
    label: "Avg Buy",
    render: (node, priceDisplay) => (
      <Td className="text-right tabular-nums">{renderPositionPrice(node, "avgLong", priceDisplay)}</Td>
    ),
  },
  {
    id: "avgSell",
    label: "Avg Sell",
    render: (node, priceDisplay) => (
      <Td className="text-right tabular-nums">{renderPositionPrice(node, "avgShort", priceDisplay)}</Td>
    ),
  },
  {
    id: "netPx",
    label: "Net Px",
    render: (node, priceDisplay) => (
      <Td
        className={cn(
          "text-right tabular-nums font-medium",
          node.netPrice == null
            ? "text-muted"
            : node.netMW > 0
              ? "text-posGreen"
              : node.netMW < 0
                ? "text-negRed"
                : "text-muted"
        )}
      >
        {renderPositionPrice(node, "netPrice", priceDisplay)}
      </Td>
    ),
  },
  {
    id: "settle",
    label: "Settle",
    render: (node, priceDisplay) => (
      <Td className="text-right tabular-nums text-fg sm:text-muted">
        <span>
          {renderPositionPrice(node, "settle", priceDisplay)}
          {node.referenceMethod !== "exact_contract" && (node.components?.length ?? 0) > 1 && (
            <span className="ml-1 text-xs" tabIndex={0} aria-label="Weighted reference" title="Hours-weighted reference across calculation periods; no exact contract settlement used">≈</span>
          )}
          {(node.referenceMarkEUR != null || node.mtmPrice != null) && node.mtmPriceDate && (
            <span className="ml-1 text-xs font-medium text-fg sm:text-[9px] sm:font-normal sm:text-inherit sm:opacity-70">
              {node.mtmPriceDate.slice(5)}
            </span>
          )}
        </span>
      </Td>
    ),
  },
  {
    id: "realized",
    label: "Realized €",
    render: (node) => <Td className="text-right tabular-nums">{fmtEur0(node.realizedEUR)}</Td>,
  },
  {
    id: "mtm",
    label: "MtM €",
    render: (node) => (
      <Td
        className={cn(
          "text-right tabular-nums",
          node.mtmEUR != null && node.mtmEUR > 0
            ? "text-posGreen"
            : node.mtmEUR != null && node.mtmEUR < 0
              ? "text-negRed"
              : ""
        )}
      >
        {fmtEur0(node.mtmEUR)}
      </Td>
    ),
  },
  {
    id: "total",
    label: "Total €",
    render: (node) => (
      <Td
        className={cn(
          "text-right tabular-nums font-semibold",
          node.totalEUR != null && node.totalEUR > 0
            ? "text-posGreen"
            : node.totalEUR != null && node.totalEUR < 0
              ? "text-negRed"
              : ""
        )}
      >
        {fmtEur0(node.totalEUR)}
      </Td>
    ),
  },
  {
    // Windowed variation margin (toolbar "VM"): cleared cash VM plain, bilateral
    // shadow VM italic; a subtotal mixing both carries the ◇ marker and the
    // split on hover. Shown only while the VM toggle is on.
    id: "vm",
    label: "VM €",
    render: (node) => {
      const vm = selectPositionVmFacts(node);
      return (
        <Td
          className={cn(
            "text-right tabular-nums",
            vm.value != null && vm.value > 0 ? "text-posGreen" : vm.value != null && vm.value < 0 ? "text-negRed" : "text-muted",
            vm.kind === "bilateral" && "italic opacity-75"
          )}
        >
          <span title={vm.title || undefined} className={cn(vm.title && "cursor-help")} data-vm-kind={vm.kind}>
            {vm.value != null ? fmtEur0(vm.value) : "—"}
            {vm.kind === "mixed" && (
              <span className="ml-0.5 text-[9px] align-super text-muted" aria-label="includes bilateral shadow VM">◇</span>
            )}
            {vm.partial && <span className="ml-0.5 text-[9px] align-super text-warnOrange" aria-label="partial">*</span>}
          </span>
        </Td>
      );
    },
  },
];

const METRIC_COL_IDS: MetricColId[] = METRIC_COLUMNS.map((c) => c.id as MetricColId);
const METRIC_COL_BY_ID: Record<MetricColId, MetricCol> = Object.fromEntries(
  METRIC_COLUMNS.map((c) => [c.id, c])
) as Record<MetricColId, MetricCol>;
// A distinct copy (not the METRIC_COL_IDS reference): this value is handed straight
// into React state (load fallback + reset), so keeping it independent guards the
// module-level catalog against any future in-place mutation of the order state.
const DEFAULT_COLUMN_ORDER: MetricColId[] = [...METRIC_COL_IDS];

// Move `fromId` next to `toId`. Dragging rightward drops AFTER the target, leftward
// drops BEFORE it — so the dragged column lands adjacent to the target on the side it
// approached from, and every slot (including the last) is reachable. Pure; returns the
// input untouched if the drag is a no-op or either id is unknown.
function reorderColumnIds(
  order: MetricColId[],
  fromId: MetricColId,
  toId: MetricColId
): MetricColId[] {
  if (fromId === toId) return order;
  const fromIdx = order.indexOf(fromId);
  const toIdx = order.indexOf(toId);
  if (fromIdx === -1 || toIdx === -1) return order;
  const without = order.filter((id) => id !== fromId);
  const targetIdx = without.indexOf(toId);
  const insertAt = fromIdx < toIdx ? targetIdx + 1 : targetIdx;
  without.splice(insertAt, 0, fromId);
  return without;
}

// Reconcile a persisted order with the current catalog: keep the saved positions
// of ids we still know (deduped), then append any known ids the save didn't list
// (e.g. a column added in a later release). Guarantees a full, valid, gap-free order.
function normalizeColumnOrder(saved: unknown): MetricColId[] {
  const known = new Set<string>(METRIC_COL_IDS);
  const fromSaved = Array.isArray(saved)
    ? saved.filter((id): id is MetricColId => typeof id === "string" && known.has(id))
    : [];
  const deduped = [...new Set(fromSaved)];
  return [...deduped, ...METRIC_COL_IDS.filter((id) => !deduped.includes(id))];
}

// ── Per-user persistence of the toolbar settings ──────────────────────────────
// The grouping order, the contract-view filter and the delivery window are kept in
// localStorage, keyed by user email, so they survive a refresh or navigating away
// and back — each user keeps their own layout on this browser. Malformed/legacy
// values fall back to the defaults; localStorage being unavailable is non-fatal.
interface TreePrefs {
  grouping: GroupDimension[];
  viewKind: ContractViewKind;
  range: { from: string; to: string };
  // Active view only: also show live positions we hold that the latest clearing
  // statement doesn't list yet (badge "NOT ON BANK"). Default on so a position
  // opened today is visible before the next statement lands.
  offBook: boolean;
  // User's drag-chosen order of the metric columns (the leading Group column is
  // pinned and not part of this). Normalized on load against the current catalog.
  columnOrder: MetricColId[];
  // Per-month profit columns (recognition attribution): on/off + the window
  // around the current month ("back:forward" in months, or "all").
  showMonths: boolean;
  monthsWindow: string;
  // Price columns only; position valuation and P&L remain server-owned EUR.
  priceDisplayMode: PriceDisplayMode;
  fxBasis: FxBasis;
  // Windowed VM column: on/off, the window preset (custom uses vmCustom) and
  // whether bilateral shadow VM is included. Off by default.
  vmEnabled: boolean;
  vmPreset: VmWindowPreset;
  vmCustom: { from: string; to: string };
  vmIncludeBilateral: boolean;
  // Which tree group paths are collapsed (see nodeKeySegment). Paths are the node's
  // own group values, not array slots, so a stale entry from a group that no longer
  // exists is simply inert — no cleanup needed, and it "comes back" if the user
  // returns to a grouping/filter where that path exists again.
  collapsedPaths: string[];
}
const PREFS_PREFIX = "eex-pos-tree-prefs:v1:";
function loadTreePrefs(key: string): TreePrefs {
  const fallback: TreePrefs = {
    grouping: DEFAULT_GROUPING,
    viewKind: "active",
    range: defaultDeliveryWindow(),
    offBook: true,
    columnOrder: DEFAULT_COLUMN_ORDER,
    showMonths: true,
    monthsWindow: "3:6",
    priceDisplayMode: "native",
    fxBasis: "forward",
    vmEnabled: false,
    vmPreset: "7D",
    vmCustom: { from: berlinToday(), to: berlinToday() },
    vmIncludeBilateral: true,
    collapsedPaths: [],
  };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const p = JSON.parse(raw) as Partial<TreePrefs>;
    const grouping =
      Array.isArray(p.grouping) &&
      p.grouping.length > 0 &&
      p.grouping.every((d) => ALL_DIMENSIONS.includes(d as GroupDimension))
        ? ([...new Set(p.grouping)] as GroupDimension[])
        : fallback.grouping;
    const viewKind =
      p.viewKind === "all" || p.viewKind === "active" || p.viewKind === "interval"
        ? p.viewKind
        : fallback.viewKind;
    const range =
      p.range && typeof p.range.from === "string" && typeof p.range.to === "string"
        ? { from: p.range.from, to: p.range.to }
        : fallback.range;
    const offBook = typeof p.offBook === "boolean" ? p.offBook : fallback.offBook;
    const columnOrder = normalizeColumnOrder(p.columnOrder);
    const showMonths = typeof p.showMonths === "boolean" ? p.showMonths : fallback.showMonths;
    const monthsWindow = ["3:6", "6:12", "all"].includes(p.monthsWindow as string)
      ? (p.monthsWindow as string)
      : fallback.monthsWindow;
    const priceDisplayMode = p.priceDisplayMode === "EUR" || p.priceDisplayMode === "native"
      ? p.priceDisplayMode
      : fallback.priceDisplayMode;
    const fxBasis = p.fxBasis === "spot" ? "spot" : "forward";
    const vmEnabled = typeof p.vmEnabled === "boolean" ? p.vmEnabled : fallback.vmEnabled;
    const vmPreset = (["1D", "7D", "MTD", "custom"] as const).includes(p.vmPreset as VmWindowPreset)
      ? (p.vmPreset as VmWindowPreset)
      : fallback.vmPreset;
    const vmCustom =
      p.vmCustom && typeof p.vmCustom.from === "string" && typeof p.vmCustom.to === "string"
        ? { from: p.vmCustom.from, to: p.vmCustom.to }
        : fallback.vmCustom;
    const vmIncludeBilateral =
      typeof p.vmIncludeBilateral === "boolean" ? p.vmIncludeBilateral : fallback.vmIncludeBilateral;
    const collapsedPaths = Array.isArray(p.collapsedPaths)
      ? [...new Set(p.collapsedPaths.filter((s): s is string => typeof s === "string"))]
      : fallback.collapsedPaths;
    return {
      grouping, viewKind, range, offBook, columnOrder, showMonths, monthsWindow, priceDisplayMode, fxBasis,
      vmEnabled, vmPreset, vmCustom, vmIncludeBilateral, collapsedPaths,
    };
  } catch {
    return fallback;
  }
}
function saveTreePrefs(key: string, prefs: TreePrefs): void {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private mode / quota) — persistence is best-effort.
  }
}

// ── Per-month Positions profit columns (owner 2026-07-05) ───────────────────
// Window-generated columns appended after the metric block: each cell shows a
// node's BOOKED cleared Recognition EUR for that month (bold) and the ESTIMATE
// (italic warning tone, ≈ — open cleared or bilateral Positions valuation; display only).
// Booked and estimate never blend. Out-of-window months roll into the ‹earlier›
// and ‹later› edge columns so every row still partitions its full lifetime P&L.
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthColLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  return `${MONTH_ABBR[Number(m) - 1] ?? m}-${y!.slice(2)}`;
};
const addMonths = (ym: string, delta: number): string => {
  const [y, m] = ym.split("-").map(Number);
  const t = y! * 12 + (m! - 1) + delta;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
};
function renderMonthCell(v: PositionMonthFacts | null): ReactNode {
  const b = v ? Math.round(v.bookedEUR) : 0;
  const e = v ? Math.round(v.estimateEUR) : 0;
  return (
    <Td className="text-right tabular-nums whitespace-nowrap">
      {b !== 0 && (
        <div className={cn("font-medium", b > 0 ? "text-posGreen" : "text-negRed")}>{fmtEur0(b)}</div>
      )}
      {e !== 0 && (
        <div
          className="text-xs font-medium italic text-warnOrange sm:text-[10px] sm:font-normal sm:text-amber-600 dark:sm:text-amber-400"
          title="estimate — open cleared or bilateral Positions valuation; not booked"
        >
          ≈ {fmtEur0(e)}
        </div>
      )}
    </Td>
  );
}
function buildMonthColumns(
  nodes: GroupedNode[],
  window: string
): MetricCol[] {
  const keys = new Set<string>();
  for (const n of nodes) if (n.months) for (const k of Object.keys(n.months)) keys.add(k);
  const all = [...keys].sort();
  if (all.length === 0) return [];
  let visible = all;
  let earlier: string[] = [];
  let later: string[] = [];
  if (window !== "all") {
    const [back, fwd] = window.split(":").map(Number);
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const lo = addMonths(cur, -(back ?? 3));
    const hi = addMonths(cur, fwd ?? 6);
    visible = all.filter((k) => k >= lo && k <= hi);
    earlier = all.filter((k) => k < lo);
    later = all.filter((k) => k > hi);
  }
  const cols: MetricCol[] = [];
  if (earlier.length > 0)
    cols.push({
      id: "m:earlier",
      label: `‹ ${monthColLabel(earlier[earlier.length - 1]!)} & earlier`,
      static: true,
      render: (n) => renderMonthCell(selectPositionMonthFacts(n, earlier)),
    });
  for (const k of visible)
    cols.push({ id: `m:${k}`, label: monthColLabel(k), static: true, render: (n) => renderMonthCell(selectPositionMonthFacts(n, [k])) });
  if (later.length > 0)
    cols.push({
      id: "m:later",
      label: `${monthColLabel(later[0]!)} & later ›`,
      static: true,
      render: (n) => renderMonthCell(selectPositionMonthFacts(n, later)),
    });
  return cols;
}

function EexPositionsTreePage() {
  // Per-user persisted toolbar settings — restored on mount (the page only renders
  // once the auth'd user is loaded, so the key is stable here), re-saved on every
  // change, so a refresh or page switch keeps the user's layout.
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const prefsKey = `${PREFS_PREFIX}${user?.email ?? "anon"}`;
  const [grouping, setGrouping] = useState<GroupDimension[]>(() => loadTreePrefs(prefsKey).grouping);
  // Contract-view filter (default "active" so year-old delivered contracts like
  // Jun-25 are hidden on first load). The interval bounds are seeded with a
  // sensible default window and only take effect when viewKind === "interval".
  const [viewKind, setViewKind] = useState<ContractViewKind>(() => loadTreePrefs(prefsKey).viewKind);
  const [range, setRange] = useState(() => loadTreePrefs(prefsKey).range);
  // Active view: include live positions not yet on a clearing statement (see TreePrefs).
  const [offBook, setOffBook] = useState(() => loadTreePrefs(prefsKey).offBook);
  // Drag-chosen order of the metric columns, persisted per user.
  const [columnOrder, setColumnOrder] = useState<MetricColId[]>(
    () => loadTreePrefs(prefsKey).columnOrder
  );
  // Per-month profit columns: toggle + window (persisted with the layout).
  const [showMonths, setShowMonths] = useState(() => loadTreePrefs(prefsKey).showMonths);
  const [monthsWindow, setMonthsWindow] = useState(() => loadTreePrefs(prefsKey).monthsWindow);
  const [priceDisplayMode, setPriceDisplayMode] = useState<PriceDisplayMode>(
    () => loadTreePrefs(prefsKey).priceDisplayMode
  );
  const [fxBasis, setFxBasis] = useState<FxBasis>(() => loadTreePrefs(prefsKey).fxBasis);
  const [vmEnabled, setVmEnabled] = useState(() => loadTreePrefs(prefsKey).vmEnabled);
  const [vmPreset, setVmPreset] = useState<VmWindowPreset>(() => loadTreePrefs(prefsKey).vmPreset);
  const [vmCustom, setVmCustom] = useState(() => loadTreePrefs(prefsKey).vmCustom);
  const [vmIncludeBilateral, setVmIncludeBilateral] = useState(() => loadTreePrefs(prefsKey).vmIncludeBilateral);
  // Which tree group paths are collapsed, persisted per user (see TreePrefs).
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(loadTreePrefs(prefsKey).collapsedPaths)
  );
  const toggleCollapsed = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  useEffect(() => {
    saveTreePrefs(prefsKey, {
      grouping,
      viewKind,
      range,
      offBook,
      columnOrder,
      showMonths,
      monthsWindow,
      priceDisplayMode,
      fxBasis,
      vmEnabled,
      vmPreset,
      vmCustom,
      vmIncludeBilateral,
      collapsedPaths: [...collapsed],
    });
  }, [prefsKey, grouping, viewKind, range, offBook, columnOrder, showMonths, monthsWindow, priceDisplayMode, fxBasis, vmEnabled, vmPreset, vmCustom, vmIncludeBilateral, collapsed]);
  // Only a VALID custom window drives the query (same last-good rule as the
  // delivery range below): an inverted custom range keeps the previous window.
  const resolvedVmWindow = vmEnabled
    ? resolveVmWindowRequest(vmPreset, vmCustom, vmIncludeBilateral, berlinToday())
    : null;
  const [appliedVmWindow, setAppliedVmWindow] = useState(resolvedVmWindow);
  const resolvedVmKey = JSON.stringify(resolvedVmWindow);
  useEffect(() => {
    if (!vmEnabled || resolvedVmWindow) setAppliedVmWindow(resolvedVmWindow);
  }, [vmEnabled, resolvedVmKey]); // keyed by value: the object is rebuilt every render
  const vmColumnLabel = vmPreset === "custom" && appliedVmWindow?.kind === "range"
    ? `VM ${appliedVmWindow.from.slice(5)}→${appliedVmWindow.to.slice(5)}`
    : `VM ${vmPreset}`;
  const orderedColumns = columnOrder
    .filter((id) => id !== "vm" || vmEnabled)
    .map((id) => (id === "vm" ? { ...METRIC_COL_BY_ID.vm, label: vmColumnLabel } : METRIC_COL_BY_ID[id]))
    .filter(Boolean);
  const reorderColumns = (fromId: MetricColId, toId: MetricColId) =>
    setColumnOrder((prev) => reorderColumnIds(prev, fromId, toId));
  const columnsCustomized = columnOrder.join() !== DEFAULT_COLUMN_ORDER.join();
  const rangeValid = range.from <= range.to;
  // Only a VALID window drives the query: while the user types an inverted range
  // (From > To) we keep the LAST GOOD window so the tree doesn't flip to a
  // different result set mid-edit (the UI flags the guard). appliedRange catches
  // up the moment the range is valid again.
  // Initialize from a VALID window: a persisted inverted range (From > To) would
  // otherwise be applied on load and render an empty tree, since the effect below
  // only catches up on valid ranges. Order the two dates rather than start inverted.
  const [appliedRange, setAppliedRange] = useState(() =>
    range.from <= range.to ? range : { from: range.to, to: range.from }
  );
  useEffect(() => {
    if (rangeValid) setAppliedRange(range);
  }, [range.from, range.to, rangeValid]);
  const view: ContractView =
    viewKind === "interval"
      ? { kind: "interval", from: appliedRange.from, to: appliedRange.to }
      : { kind: viewKind };
  // The leaf the user clicked into; null = modal closed.
  const [leafReq, setLeafReq] = useState<LeafRequest | null>(null);

  // Build the leaf request from a clicked contract-leaf node. Every leg in an
  // economic leaf shares ref, bucket and clearing origin.
  const openLeaf = (node: GroupedNode) => {
    const request = selectLeafRequestFromNode(node, MODE);
    if (request) setLeafReq(request);
  };

  // Open the drill-down for the contract a cascade-lineage tag points at. The
  // carried lot keeps the clicked leaf's clearing origin while the ref walks to
  // its parent contract (month → quarter → year).
  const openLeafByRef = (
    ref: { code: string; expYear: number; expMonth: number },
    bucket: ProvenanceBucket,
    tradeOrigin: TradeOrigin,
    currency: "EUR" | "GBP" | "RON" | null = null,
    settlementIndex: string | null = null
  ) => {
    setLeafReq({
      code: ref.code,
      expYear: ref.expYear,
      expMonth: ref.expMonth,
      mode: MODE,
      tradeOrigin,
      currency,
      settlementIndex,
      bucket,
    });
  };

  const treeRequest: PositionsTreeRequest = {
    grouping,
    mode: MODE,
    view,
    includeOffBook: offBook,
    includeMonths: showMonths,
    fxBasis,
    vmWindow: appliedVmWindow,
  };
  const treeQ = useQuery({
    queryKey: positionsTreeQueryKeys.tree(treeRequest),
    queryFn: async () => {
      const result = await api<GroupedTreeResponse>(buildPositionsTreeRequestPath(treeRequest));
      if ((result.fxBasis ?? "forward") !== fxBasis) {
        throw new Error("The server returned a different FX valuation basis. Recalculate to retry.");
      }
      return result;
    },
    // Keep prior rows for layout/filter changes only. Never display one basis's
    // P&L beneath another basis's prices while the replacement request is pending.
    placeholderData: (previous) => (previous?.fxBasis ?? "forward") === fxBasis ? previous : undefined,
  });
  const nodes = treeQ.data?.nodes ?? [];
  const monthCols = useMemo(
    () => (showMonths ? buildMonthColumns(nodes, monthsWindow) : []),
    [nodes, showMonths, monthsWindow]
  );
  const vmSummary = vmEnabled ? treeQ.data?.vmWindowSummary : undefined;
  const unmappedProducts = treeQ.data?.unmappedProducts ?? [];
  const excludedTrades = treeQ.data?.excludedTrades ?? [];
  const settlementFeed = treeQ.data?.settlementFeed;
  const priceDisplay: PriceDisplayContext = {
    mode: priceDisplayMode,
    fxBasis: treeQ.data?.fxBasis ?? "forward",
    currency: treeQ.data?.currencyDisplay ?? EMPTY_CURRENCY_DISPLAY,
  };

  // Re-fetch positions from the server — the route recomputes them from the CURRENT
  // trades, so edits made on another page/tab show up. Also invalidates any open
  // leaf drill-down so it re-values too.
  const recalc = () => {
    void treeQ.refetch();
    void queryClient.invalidateQueries({ queryKey: positionsTreeQueryKeys.leafRoot() });
  };

  const moveDim = (i: number, dir: -1 | 1) =>
    setGrouping((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const a = prev[i];
      const b = prev[j];
      if (a === undefined || b === undefined) return prev;
      const next = [...prev];
      next[i] = b;
      next[j] = a;
      return next;
    });
  const removeDim = (dim: GroupDimension) =>
    setGrouping((prev) => (prev.length <= 1 ? prev : prev.filter((d) => d !== dim)));
  const addDim = (dim: GroupDimension) =>
    setGrouping((prev) => (prev.includes(dim) ? prev : [...prev, dim]));

  const inactive = ALL_DIMENSIONS.filter((d) => !grouping.includes(d));

  return (
    <div className="operations-workspace ops-page ops-positions min-w-0 max-w-full space-y-4 px-3 py-4 sm:space-y-6 sm:p-4">
      {treeQ.data?.recognitionScopeNotice && <p role="note" aria-label="Recognition scope" className="rounded border border-warnOrange/40 bg-warnOrange/10 p-3 text-sm">{treeQ.data.recognitionScopeNotice}</p>}
      <header className="ops-heading ops-position-heading flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="min-w-0 sm:mr-auto">
          <h1 className="font-semibold text-lg">
            EEX Positions ▸ Tree
            {treeQ.isFetching && !treeQ.isLoading && (
              <span className="ml-2 text-xs font-medium text-fg sm:text-[10px] sm:font-normal sm:text-muted">updating…</span>
            )}
          </h1>
          <p className="text-muted text-xs max-w-3xl">
            Configurable N-level roll-up tree — choose the grouping dimensions and their
            order, then drill down to the per-contract leaves. Per-side averages live only
            at the leaves (no blended roll-up prices). The view filter hides settled
            contracts: <strong>Active</strong> keeps only contracts on the latest clearing
            statement (tick <em>Include not-yet-cleared</em> to also show live positions you
            hold that haven't reached a statement yet, e.g. opened today),{" "}
            <strong>Delivery window</strong> keeps contracts whose delivery period overlaps
            the chosen dates (subtotals reflect only the kept contracts).
          </p>
        </div>
        <div className="flex min-w-0 flex-col items-stretch gap-0.5 sm:items-end">
          <Link to="/eex/recognition-schedules" className="min-h-11 py-2 text-sm text-accent hover:underline sm:min-h-0">Trades &amp; recognition schedules</Link>
          <button
            type="button"
            onClick={recalc}
            disabled={treeQ.isFetching}
            title="Re-fetch positions from the server — recomputed from the current trades. Use after editing trades on another page."
            className="inline-flex min-h-11 touch-manipulation items-center justify-center gap-1.5 rounded border border-border/60 px-3 py-2 text-sm hover:bg-border/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default disabled:opacity-50 sm:min-h-0 sm:px-2.5 sm:py-1 sm:text-xs"
          >
            <span className={cn("inline-block", treeQ.isFetching && "animate-spin")} aria-hidden="true">
              ⟳
            </span>
            {treeQ.isFetching ? "Recalculating…" : "Recalculate"}
          </button>
          {treeQ.dataUpdatedAt > 0 && (
            <span className="text-xs font-medium text-fg sm:text-[10px] sm:font-normal sm:text-muted">
              as of {new Date(treeQ.dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <ViewToggle
          viewKind={viewKind}
          onChange={setViewKind}
          range={range}
          onRangeChange={setRange}
          rangeValid={rangeValid}
          offBook={offBook}
          onOffBookChange={setOffBook}
        />
        <div className="ops-currency-controls flex min-w-0 flex-wrap items-center gap-3">
          <CurrencyToggle value={priceDisplayMode} onChange={setPriceDisplayMode} />
          <FxBasisToggle value={fxBasis} onChange={setFxBasis} />
        </div>
      </header>

      <p className="text-xs text-muted" aria-live="polite">
        {fxBasis === "forward"
          ? "Forward FX: RON prices and bilateral position estimates use delivery-period rates; completed months use actual FX."
          : "Spot FX: RON prices and bilateral position estimates use the latest BNR spot rate."}
        {" "}Booked Recognition P&amp;L stays at its recorded FX.
      </p>

      {settlementFeed?.status === "ready" && (
        <div className="min-w-0 break-words rounded border border-posGreen/40 bg-posGreen/10 px-3 py-2 text-xs font-medium text-posGreen sm:border-emerald-500/30 sm:bg-emerald-500/5 sm:font-normal sm:text-emerald-700 dark:sm:text-emerald-300">
          ECC settlements current through {settlementFeed.latestImportedBusinessDate}.
        </div>
      )}

      {settlementFeed?.status === "catching_up" && (
        <div className="min-w-0 break-words rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-800 dark:sm:text-amber-200">
          ECC settlements are catching up: imported through {settlementFeed.latestImportedBusinessDate ?? "none"},
          latest published {settlementFeed.latestDiscoveredBusinessDate ?? "unknown"}. ECC-derived marks are
          hidden until the feed is current; bank, manual, and datasource prices remain available.
        </div>
      )}

      {settlementFeed?.status === "unavailable" && (
        <div className="min-w-0 break-words rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-800 dark:sm:text-amber-200">
          ECC settlement status is unavailable or stale. ECC-derived marks are hidden until the public feed is
          checked successfully; bank, manual, and datasource prices remain available.
        </div>
      )}

      <GroupingBar
        grouping={grouping}
        inactive={inactive}
        onMove={moveDim}
        onRemove={removeDim}
        onAdd={addDim}
      />

      {treeQ.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {treeQ.isError && (
        <div className="text-negRed text-sm">{(treeQ.error as Error).message}</div>
      )}

      {!treeQ.isLoading && !treeQ.isError && unmappedProducts.length > 0 && (
        <UnmappedProductsBanner products={unmappedProducts} />
      )}

      {!treeQ.isLoading && !treeQ.isError && excludedTrades.length > 0 && (
        <ExcludedTradesBanner trades={excludedTrades} />
      )}

      {!treeQ.isLoading && !treeQ.isError && (
        <div className="flex min-w-0 flex-wrap items-center gap-3 text-xs sm:flex-nowrap">
          <label className="flex min-h-11 cursor-pointer touch-manipulation select-none items-center gap-2 sm:min-h-0 sm:gap-1.5">
            <input type="checkbox" checked={showMonths} onChange={(e) => setShowMonths(e.target.checked)} />
            <span>Month P&amp;L columns</span>
          </label>
          {showMonths && (
            <select
              value={monthsWindow}
              onChange={(e) => setMonthsWindow(e.target.value)}
              className="min-h-11 rounded border border-border/60 bg-transparent px-2 py-1 text-base sm:min-h-0 sm:px-1.5 sm:py-0.5 sm:text-xs"
              title="window around the current month; months outside roll into the edge columns"
            >
              <option value="3:6">−3 / +6 months</option>
              <option value="6:12">−6 / +12 months</option>
              <option value="all">all months</option>
            </select>
          )}
          {showMonths && (
            <span className="min-w-0 basis-full break-words text-xs text-fg sm:basis-auto sm:text-[10px] sm:text-muted">
              bold = booked · <span className="font-medium italic text-warnOrange sm:font-normal sm:text-amber-600 dark:sm:text-amber-400">≈ italic</span> = estimate
              (display only) · EUR-valued rows&apos; months sum to Total € ·{" "}
              <span
                className="cursor-help underline decoration-dotted"
                title="Cleared rows use C1 Recognition timing: real×real closes book in the closing trade's month; cascades carry original basis into child delivery months; open net is estimated at the current mark. Bilateral rows remain outside Recognition and contribute estimates in their delivery months. RON bilateral estimates use the selected FX valuation basis. Pair labels do not create Recognition matching."
              >
                how months are timed
              </span>
            </span>
          )}
        </div>
      )}
      {!treeQ.isLoading && !treeQ.isError && (
        <VmWindowControls
          enabled={vmEnabled}
          onEnabled={setVmEnabled}
          preset={vmPreset}
          onPreset={setVmPreset}
          custom={vmCustom}
          onCustom={setVmCustom}
          includeBilateral={vmIncludeBilateral}
          onIncludeBilateral={setVmIncludeBilateral}
          summary={vmSummary}
        />
      )}
      {!treeQ.isLoading && !treeQ.isError && (
        <TreeTable
          nodes={nodes}
          grouping={grouping}
          columns={[...orderedColumns, ...monthCols]}
          priceDisplay={priceDisplay}
          onReorderColumns={reorderColumns}
          onResetColumns={() => setColumnOrder(DEFAULT_COLUMN_ORDER)}
          columnsCustomized={columnsCustomized}
          onOpenLeaf={openLeaf}
          onOpenLeafRef={openLeafByRef}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      )}

      {leafReq && (
        <LeafModal
          req={{ ...leafReq, fxBasis }}
          onClose={() => setLeafReq(null)}
          onNavigate={openLeafByRef}
        />
      )}
    </div>
  );
}

// Toolbar row for the windowed VM column: toggle, window preset, bilateral
// inclusion and the window's comparison with the bank statements.
function VmWindowControls(props: {
  enabled: boolean;
  onEnabled: (v: boolean) => void;
  preset: VmWindowPreset;
  onPreset: (v: VmWindowPreset) => void;
  custom: { from: string; to: string };
  onCustom: (v: { from: string; to: string }) => void;
  includeBilateral: boolean;
  onIncludeBilateral: (v: boolean) => void;
  summary: VmWindowSummary | undefined;
}) {
  const { enabled, preset, custom, summary } = props;
  const customInvalid = preset === "custom" && !(custom.from && custom.to && custom.from <= custom.to);
  const control = "min-h-11 rounded border border-border/60 bg-transparent px-2 py-1 text-base sm:min-h-0 sm:px-1.5 sm:py-0.5 sm:text-xs";
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-3 text-xs" data-testid="vm-window-controls">
      <label className="flex min-h-11 cursor-pointer touch-manipulation select-none items-center gap-2 sm:min-h-0 sm:gap-1.5">
        <input type="checkbox" checked={enabled} onChange={(e) => props.onEnabled(e.target.checked)} />
        <span>VM column</span>
      </label>
      {enabled && (
        <>
          <select
            aria-label="VM window"
            value={preset}
            onChange={(e) => props.onPreset(e.target.value as VmWindowPreset)}
            className={control}
            title="Variation margin from imported ECC settlements; bank statements reconcile available days"
          >
            <option value="1D">Latest settlement day</option>
            <option value="7D">Last 7 days</option>
            <option value="MTD">Month to date</option>
            <option value="custom">Custom…</option>
          </select>
          {preset === "custom" && (
            <span className="flex items-center gap-1">
              <input
                type="date"
                aria-label="VM window from"
                value={custom.from}
                onChange={(e) => props.onCustom({ ...custom, from: e.target.value })}
                className={control}
              />
              <span className="text-muted">→</span>
              <input
                type="date"
                aria-label="VM window to"
                value={custom.to}
                onChange={(e) => props.onCustom({ ...custom, to: e.target.value })}
                className={control}
              />
              {customInvalid && <span className="text-negRed">From must be on or before To</span>}
            </span>
          )}
          <label
            className="flex min-h-11 cursor-pointer touch-manipulation select-none items-center gap-2 sm:min-h-0 sm:gap-1.5"
            title="Bilateral deals have no clearing VM; this adds their change in mark-to-market against the exchange settlement (italic, not cash)"
          >
            <input
              type="checkbox"
              checked={props.includeBilateral}
              onChange={(e) => props.onIncludeBilateral(e.target.checked)}
            />
            <span>Include bilateral <span className="italic opacity-75">(shadow VM)</span></span>
          </label>
          {summary && <VmSummaryChip summary={summary} />}
        </>
      )}
    </div>
  );
}

function VmSummaryChip({ summary }: { summary: VmWindowSummary }) {
  if (summary.effectiveTo == null) {
    return <span className="text-muted" data-testid="vm-summary">No imported settlements in this window</span>;
  }
  // Older API responses may overlap briefly with new assets during a rolling release.
  const settlementDays = summary.settlementDays ?? summary.statementDays;
  const provisionalDays = summary.provisionalDays ?? (summary.tie === "no_bank" ? settlementDays : 0);
  const reconciledDays = summary.reconciledDays ?? (summary.tie === "match" ? settlementDays : 0);
  const days = `${settlementDays} settlement day${settlementDays === 1 ? "" : "s"}`;
  const through = ` · ${summary.from === summary.effectiveTo ? summary.effectiveTo : `${summary.from} → ${summary.effectiveTo}`}`;
  const hidden = Math.round((summary.clearedTotal - summary.clearedShown) * 100) / 100;
  const tie =
    summary.tie === "match"
      ? { text: "reconciled · ties to bank", tone: "text-posGreen" }
      : summary.tie === "mismatch"
        ? { text: `bank discrepancy${summary.diff != null ? ` · net Δ ${fmtEur2(summary.diff)}` : ""}${provisionalDays ? ` · ${provisionalDays} provisional` : ""}`, tone: "text-negRed" }
        : summary.tie === "incomplete"
          ? { text: "missing settlement prices — total partial", tone: "text-warnOrange" }
          : summary.tie === "partial_view"
            ? { text: "your visible trades only — no bank comparison", tone: "text-muted" }
            : { text: `${reconciledDays ? `${reconciledDays} reconciled · ` : ""}${provisionalDays} provisional · bank statement pending`, tone: "text-warnOrange" };
  const title = [
    `Window ${summary.from} → ${summary.to} (${days})`,
    `Calculated cleared VM: €${fmtEur2(summary.clearedTotal)}`,
    summary.bankTotal != null ? `Bank statements (${summary.statementDays} days): €${fmtEur2(summary.bankTotal)}` : null,
    summary.comparedTotal != null ? `Calculated VM on those same days: €${fmtEur2(summary.comparedTotal)}` : null,
    ...(summary.reconciliation ?? []).map((d) => `${d.date}: ${d.status}${d.diff != null ? ` · Δ €${fmtEur2(d.diff)}` : ""}`),
    summary.bilateralTotal != null ? `Bilateral shadow VM (not cash): €${fmtEur2(summary.bilateralTotal)}` : null,
    hidden !== 0 ? `€${fmtEur2(hidden)} cleared VM sits on rows hidden by the current view` : null,
  ].filter(Boolean).join("\n");
  return (
    <span className="min-w-0 break-words text-muted" title={title} data-testid="vm-summary">
      {days}{through} · calculated VM €{fmtEur0(summary.clearedTotal)} · <span className={tie.tone}>{tie.text}</span>
      {hidden !== 0 && <span> · €{fmtEur0(hidden)} on hidden rows</span>}
    </span>
  );
}

function FxBasisToggle({ value, onChange }: { value: FxBasis; onChange: (value: FxBasis) => void }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">FX</span>
      <div role="group" aria-label="FX valuation basis" className="ops-segmented flex overflow-hidden rounded border border-border/60 text-xs">
        {(["spot", "forward"] as const).map((basis) => (
          <button
            key={basis}
            type="button"
            aria-pressed={value === basis}
            onClick={() => onChange(basis)}
            title={basis === "forward"
              ? "Convert RON prices and value bilateral positions using delivery-period FX rates"
              : "Convert RON prices and value bilateral positions using the latest BNR spot rate"}
            className={cn(
              "min-h-11 touch-manipulation px-3 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent sm:min-h-0 sm:px-2 sm:py-0.5 sm:text-xs",
              value === basis ? "bg-accent text-bg sm:text-white" : "text-fg hover:bg-accent/10 sm:text-muted"
            )}
          >{basis === "forward" ? "Forward" : "Spot"}</button>
        ))}
      </div>
    </div>
  );
}

function CurrencyToggle({
  value,
  onChange,
}: {
  value: PriceDisplayMode;
  onChange: (value: PriceDisplayMode) => void;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">
        Prices
      </div>
      <div className="ops-segmented flex overflow-hidden rounded border border-border/60 text-xs">
        {(["EUR", "native"] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={value === mode}
            onClick={() => onChange(mode)}
            title={mode === "EUR" ? "Show every convertible transaction and settlement price in EUR" : "Show transaction prices in their native currency; non-EUR prices are italic"}
            className={cn(
              "min-h-11 touch-manipulation px-3 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent sm:min-h-0 sm:px-2 sm:py-0.5 sm:text-xs",
              value === mode ? "bg-accent text-bg sm:text-white" : "text-fg hover:bg-accent/10 sm:text-muted"
            )}
          >
            {mode === "EUR" ? "EUR" : "Native"}
          </button>
        ))}
      </div>
    </div>
  );
}

// Contract-view filter control: a 3-option segmented pill (All / Active /
// Delivery window), plus inline From/To date inputs revealed only in interval
// mode. From <= To is guarded inline.
function ViewToggle({
  viewKind,
  onChange,
  range,
  onRangeChange,
  rangeValid,
  offBook,
  onOffBookChange,
}: {
  viewKind: ContractViewKind;
  onChange: (k: ContractViewKind) => void;
  range: { from: string; to: string };
  onRangeChange: (r: { from: string; to: string }) => void;
  rangeValid: boolean;
  offBook: boolean;
  onOffBookChange: (b: boolean) => void;
}) {
  const opts: { key: ContractViewKind; label: string; title?: string }[] = [
    { key: "all", label: "All", title: "every contract, including settled ones" },
    {
      key: "active",
      label: "Active",
      title: "only contracts on the latest clearing statement",
    },
    {
      key: "interval",
      label: "Delivery window",
      title: "contracts whose delivery period overlaps the chosen dates",
    },
  ];
  return (
    <div className="ops-view-controls flex min-w-0 flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:flex-nowrap sm:items-center">
      <div className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">View</div>
      <div className="ops-segmented flex min-w-0 overflow-hidden rounded border border-border/60 text-xs">
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            title={o.title}
            onClick={() => onChange(o.key)}
            className={cn(
              "min-h-11 flex-1 touch-manipulation px-2 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent sm:min-h-0 sm:flex-none sm:py-0.5 sm:text-xs",
              viewKind === o.key
                ? "bg-accent text-bg sm:text-white"
                : "text-fg hover:bg-accent/10 sm:text-muted sm:hover:bg-transparent sm:hover:text-fg"
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      {viewKind === "active" && (
        <label
          className="flex min-h-11 cursor-pointer touch-manipulation select-none items-center gap-2 text-sm font-medium text-fg sm:min-h-0 sm:gap-1 sm:text-xs sm:font-normal sm:text-muted"
          title="Also show live positions you hold that the latest clearing statement doesn't list yet (e.g. opened today). They badge NOT ON BANK until the next statement carries them."
        >
          <input
            type="checkbox"
            checked={offBook}
            onChange={(e) => onOffBookChange(e.target.checked)}
            className="accent-accent"
          />
          <span>Include not-yet-cleared</span>
        </label>
      )}
      {viewKind === "interval" && (
        <div className="grid min-w-0 grid-cols-1 gap-2 text-xs sm:flex sm:items-center sm:gap-1">
          <label className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2 text-fg sm:flex sm:gap-1 sm:text-muted">
            <span className="text-xs font-medium uppercase tracking-wide sm:text-[10px] sm:font-normal">From</span>
            <input
              type="date"
              value={range.from}
              max={range.to}
              onChange={(e) => onRangeChange({ ...range, from: e.target.value })}
              className="min-h-11 min-w-0 rounded border border-border/60 bg-bg px-2 py-1 text-base text-fg sm:min-h-0 sm:px-1 sm:py-0.5 sm:text-xs"
            />
          </label>
          <label className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2 text-fg sm:flex sm:gap-1 sm:text-muted">
            <span className="text-xs font-medium uppercase tracking-wide sm:text-[10px] sm:font-normal">To</span>
            <input
              type="date"
              value={range.to}
              min={range.from}
              onChange={(e) => onRangeChange({ ...range, to: e.target.value })}
              className="min-h-11 min-w-0 rounded border border-border/60 bg-bg px-2 py-1 text-base text-fg sm:min-h-0 sm:px-1 sm:py-0.5 sm:text-xs"
            />
          </label>
          {!rangeValid && (
            <span className="text-xs font-medium text-negRed sm:text-[10px] sm:font-normal" title="From must be on or before To">
              From ≤ To
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function GroupingBar({
  grouping,
  inactive,
  onMove,
  onRemove,
  onAdd,
}: {
  grouping: GroupDimension[];
  inactive: GroupDimension[];
  onMove: (i: number, dir: -1 | 1) => void;
  onRemove: (dim: GroupDimension) => void;
  onAdd: (dim: GroupDimension) => void;
}) {
  return (
    <div className="ops-toolbar ops-grouping min-w-0 space-y-2 rounded-md border border-border/60 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">
        Grouping — outermost first, leaves below the last level
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {grouping.map((dim, i) => (
          <div
            key={dim}
            className="flex min-w-0 items-center gap-1 rounded border border-accent/50 bg-accent/5 pl-2 pr-1 text-xs sm:py-1"
          >
            <span className="text-xs font-medium tabular-nums text-fg sm:text-[10px] sm:font-normal sm:text-muted">{i + 1}.</span>
            <span className="text-fg">{DIM_LABELS[dim]}</span>
            <button
              type="button"
              onClick={() => onMove(i, -1)}
              disabled={i === 0}
              title="Move up (outer)"
              className="inline-flex h-11 w-11 touch-manipulation items-center justify-center text-fg outline-none hover:text-accent focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-30 sm:h-auto sm:w-auto sm:p-0 sm:text-muted sm:hover:text-fg"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => onMove(i, 1)}
              disabled={i === grouping.length - 1}
              title="Move down (inner)"
              className="inline-flex h-11 w-11 touch-manipulation items-center justify-center text-fg outline-none hover:text-accent focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-30 sm:h-auto sm:w-auto sm:p-0 sm:text-muted sm:hover:text-fg"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => onRemove(dim)}
              disabled={grouping.length <= 1}
              title="Remove dimension"
              className="inline-flex h-11 w-11 touch-manipulation items-center justify-center text-negRed outline-none hover:text-negRed focus-visible:ring-2 focus-visible:ring-negRed disabled:opacity-30 sm:h-auto sm:w-auto sm:p-0 sm:hover:text-negRed/80"
            >
              ✕
            </button>
          </div>
        ))}
        {inactive.length > 0 && (
          <div className="flex min-w-0 flex-wrap items-center gap-1 sm:ml-2 sm:flex-nowrap">
            <span className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">Add</span>
            {inactive.map((dim) => (
              <button
                key={dim}
                type="button"
                onClick={() => onAdd(dim)}
                title={`Add ${DIM_LABELS[dim]}`}
                className="min-h-11 touch-manipulation rounded border border-border/60 px-3 py-2 text-sm font-medium text-fg outline-none hover:border-accent/50 hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent sm:min-h-0 sm:px-2 sm:py-1 sm:text-xs sm:font-normal sm:text-muted sm:hover:bg-transparent sm:hover:text-fg"
              >
                + {DIM_LABELS[dim]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// "← from {originLabel}" — a CASCADED-IN slice: these lots were traded as the parent
// contract, then cascaded into this child on the parent's last trading day. Used on a
// pure-cascade single-slice leaf AND on a "← from X" sub-row. When `onDrill` is given,
// the tag links into the parent's own leaf. `value` of the row stays the leaf's OWN
// contract label; the tag carries the lineage.
function LineageTag({ originLabel, onDrill }: { originLabel: string; onDrill?: () => void }) {
  const drillProps = onDrill
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick: (e: React.MouseEvent) => {
          // Don't let the click bubble into the row's own drill-down — the tag
          // opens the PARENT contract, not this leaf.
          e.stopPropagation();
          onDrill();
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onDrill();
          }
        },
      }
    : {};
  return (
    <span
      {...drillProps}
      title={`cascaded in from ${originLabel} — these lots were traded as ${originLabel}, then replicated into this child on the parent's last trading day carrying their ORIGINAL basis (this is the acquisition cost the desk holds; the clearing bank books the child at the cascade settlement S_F)${
        onDrill ? ` · click to open ${originLabel}` : ""
      }`}
      className={cn(
        "ml-1.5 rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium tracking-wide text-accent sm:text-[10px] sm:font-normal",
        onDrill &&
          "inline-flex min-h-11 touch-manipulation items-center cursor-pointer underline decoration-dotted underline-offset-2 outline-none hover:bg-accent/20 focus-visible:ring-2 focus-visible:ring-accent sm:inline sm:min-h-0"
      )}
    >
      ← from {originLabel}
    </span>
  );
}

// "direct fills" — the directly-traded slice of a mixed leaf: genuine trades on THIS
// contract, as opposed to cascaded-in parcels (← from X) or the self-close (↻).
function DirectFillsTag() {
  return (
    <span
      title="directly traded on this contract — genuine fills, not cascaded in and not the cascade close"
      className="ml-1.5 rounded bg-border/30 px-1.5 py-0.5 text-xs font-medium tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted"
    >
      direct fills
    </span>
  );
}

// Clearing provenance is economic, unlike the cascade-lineage tags above. Both
// values are explicit so two otherwise-identical terminal rows remain legible.
function TradeOriginChip({ origin }: { origin: TradeOrigin }) {
  const bilateral = origin === "bilateral";
  return (
    <span
      title={
        bilateral
          ? "Bilateral position — valued internally, excluded from ECC bank reconciliation, VM and Recognition"
          : "Cleared EEX position — included in ECC bank reconciliation, VM and Recognition"
      }
      className={cn(
        "ml-1 rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide sm:text-[9px]",
        bilateral
          ? "bg-warnOrange/15 text-warnOrange sm:bg-amber-500/15 sm:text-amber-700 dark:sm:text-amber-400"
          : "bg-accent/10 text-accent sm:bg-sky-500/15 sm:text-sky-700 dark:sm:text-sky-400"
      )}
    >
      {bilateral ? "BIL" : "CLEARED"}
    </span>
  );
}

// "→ into {rollToLabel}" — the C1 look-through roll: on its last trading day this
// contract's open net REPLICATES into its children carrying its ORIGINAL basis, and
// realizes NOTHING here (e.g. a quarter → "→ into Apr–Jun 26"). The directional
// counterpart of "← from X". NOT a trade — it is why a dissolved quarter/year nets to
// 0 AND shows Realized 0; the P&L rides forward and is recognized on the children in
// their delivery months. (The net column shows the leg's own closing sign — opposite
// of what re-opens downstream.) Falls back to a generic label when the destination is
// unknown.
function CascadeCloseTag({ to }: { to?: string | null }) {
  return (
    <span
      title={`replicated into${
        to ? ` ${to}` : " its delivery months"
      } at its ORIGINAL basis — nothing is realized here (C1 look-through). The P&L rides forward and is recognized on the children as they deliver. A synthetic flatten, not a trade; the net shown is the closing leg.`}
      className="ml-1.5 rounded bg-warnOrange/15 px-1.5 py-0.5 text-xs font-medium tracking-wide text-warnOrange sm:bg-amber-500/15 sm:text-[10px] sm:font-normal sm:text-amber-700 dark:sm:text-amber-400"
    >
      → into {to ?? "delivery months"}
    </span>
  );
}

function LifecycleTag({
  lifecycle,
  deliveredFraction,
  netMW,
}: {
  lifecycle: Lifecycle | null;
  deliveredFraction: number | null;
  netMW: number;
}) {
  // The badge describes an OPEN position moving through its contract's life
  // (delivering / expired / cascaded). A flat book (net 0) carries nothing into
  // any of those phases — the contract's calendar state is irrelevant to a
  // closed round-trip — so it must not be labeled "in delivery" etc. The row is
  // still shown (it's on the clearing-bank statement); only the badge is dropped.
  if (netMW === 0) return null;
  if (lifecycle === "in_delivery") {
    const pct = deliveredFraction != null ? Math.round(deliveredFraction * 100) : null;
    const locked = pct != null && pct >= 100;
    const title =
      pct == null
        ? "in the delivery period"
        : locked
          ? "fully delivered / locked — gas settles at a fixed FSP, no remaining price risk"
          : `${pct}% delivered — that share of the P&L is locked (realized); the rest is still marked to market. Pro-rata estimate.`;
    return (
      <span
        title={title}
        className="ml-1.5 rounded bg-warnOrange/15 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-warnOrange sm:bg-amber-500/15 sm:text-[10px] sm:font-normal sm:text-amber-600 dark:sm:text-amber-400"
      >
        in delivery{pct != null && !locked ? ` · ${pct}%` : ""}
      </span>
    );
  }
  if (lifecycle === "expired") {
    return (
      <span className="ml-1.5 rounded bg-border/30 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">
        expired
      </span>
    );
  }
  if (lifecycle === "cascaded") {
    return (
      <span
        title="quarters/seasons/years never deliver — replaced by their child contracts on their last trading day"
        className="ml-1.5 rounded bg-border/30 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted"
      >
        cascaded
      </span>
    );
  }
  return null;
}

// Provenance-attribute chips on a contract leaf: surface a bucket field that
// DISTINGUISHES this leaf but is NOT encoded by the current grouping path — so two
// rows that look identical (same contract + same grouped dims) but are separate
// leaves show WHY (e.g. one is tagged to a spread `pair`, the other isn't). Only
// `pair` is surfaced on the tree: it is the SPARSE, economically-meaningful tag (a
// spread/structured-trade label), so it appears on just the few leaves that carry
// one and never as row-by-row noise. The dense fields (book/trader/broker/venue are
// almost always present) would clutter every row — the full five-field set still
// shows in the drill-down's BucketChips. A chip is rendered only when the field is
// non-null AND that dimension is not itself a grouping level (else it is redundant
// with its parent group node). `grouping.includes` keeps this self-suppressing: add
// `pair` to the grouping and the chip disappears.
// Element type is bounded to the dims that are BOTH a provenance-bucket key AND a
// grouping dimension (book/pair/trader/venue) — so `bucket[dim]` always type-checks
// and the compiler REJECTS at the declaration site any dim that isn't a bucket field
// (e.g. account/deliveryPeriod/product) rather than failing deep in the loop. broker
// is a bucket key but not a GroupDimension, so it is (correctly) excluded.
const LEAF_ATTR_DIMS = ["pair"] as const satisfies readonly (keyof ProvenanceBucket &
  GroupDimension)[];

function LeafAttrChips({
  bucket,
  grouping,
}: {
  bucket: ProvenanceBucket | undefined;
  grouping: GroupDimension[];
}) {
  if (!bucket) return null;
  const chips = LEAF_ATTR_DIMS.flatMap((dim) => {
    const value = bucket[dim];
    if (!value || grouping.includes(dim)) return [];
    return [{ dim, value }];
  });
  if (chips.length === 0) return null;
  return (
    <>
      {chips.map((c) => (
        <span
          key={c.dim}
          title={`${DIM_LABELS[c.dim]} ${c.value} — this is a separate position because its ${c.dim} differs (${c.dim} is not a grouping level). Group by ${DIM_LABELS[c.dim]} to fold these together.`}
          className="ml-1.5 rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium tracking-wide text-accent sm:bg-violet-500/15 sm:text-[10px] sm:font-normal sm:text-violet-600 dark:sm:text-violet-400"
        >
          <span className="font-semibold uppercase sm:font-normal sm:opacity-70">{DIM_LABELS[c.dim]}</span>{" "}
          <span className="inline-block max-w-[12rem] truncate align-bottom">{c.value}</span>
        </span>
      ))}
    </>
  );
}

// SAFETY NET banner: products the server couldn't map to any family were dropped from
// the tree (they'd otherwise vanish without a trace). Show them — product string + gross
// lots + trade count — so a new/exotic product surfaces loudly instead of silently. Only
// rendered when the list is non-empty, so it stays invisible in the all-mapped common case.
function UnmappedProductsBanner({ products }: { products: UnmappedProduct[] }) {
  const totalLots = products.reduce((s, p) => s + p.lots, 0);
  return (
    <div className="rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-700 dark:sm:text-amber-300">
      <p className="font-semibold">
        ⚠ {products.length} unmapped {products.length === 1 ? "product" : "products"} not shown
        {" "}({fmtQty(totalLots)} lots)
      </p>
      <p className="mt-0.5 text-warnOrange sm:text-amber-700/80 dark:sm:text-amber-300/80">
        These products matched no known EEX family, so they were dropped from the tree (not
        netted into any position). Add their mapping in <code>product-codes.ts</code> to make
        them appear.
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {products.map((p) => (
          <li key={p.product} className="flex items-baseline gap-2">
            <span className="font-mono break-all">{p.product}</span>
            <span className="whitespace-nowrap text-warnOrange sm:text-amber-700/70 dark:sm:text-amber-300/70">
              {fmtQty(p.lots)} lots · {p.trades} {p.trades === 1 ? "trade" : "trades"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExcludedTradesBanner({ trades }: { trades: ExcludedTrade[] }) {
  return (
    <div
      role="alert"
      className="min-w-0 break-words rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs font-medium text-warnOrange sm:border-amber-500/40 sm:bg-amber-500/10 sm:font-normal sm:text-amber-700 dark:sm:text-amber-300"
    >
      <p className="font-semibold">
        {trades.length} {trades.length === 1 ? "trade excluded" : "trades excluded"} from Positions
        {" "}— totals incomplete
      </p>
      <p className="mt-0.5">
        The delivery periods below could not be included. Position quantities and P&amp;L
        totals are incomplete.
      </p>
      <ul className="mt-1.5 space-y-1">
        {trades.map((trade, index) => (
          <li key={`${trade.tradeId ?? "trade"}:${index}`}>
            <span className="font-semibold">{trade.product}</span>
            {" · "}{trade.period || "No delivery period"}
            {" · "}{trade.signedQty > 0 ? "+" : ""}{fmtQty(trade.signedQty)} MW
            {" · "}{trade.reason === "invalid_period"
              ? "Delivery dates could not be read"
              : "Delivery period is not supported"}
            {trade.tradeId && <span className="block break-all text-[11px]">{trade.tradeId}</span>}
          </li>
        ))}
      </ul>
      <a className="mt-2 inline-block underline underline-offset-2" href="/eex/trades">
        View trades
      </a>
    </div>
  );
}

// Per-contract, lifecycle-aware reconciliation status surfaced on every
// single-contract node (leaf or slice). States:
//   ties     → green  "✓ bank" / "✓ flat"      — confirmed net matches the bank's last close, nothing pending.
//   pending  → split  "✓ {n} bank | {m} uncleared" — confirmed net ties the bank; {m} traded since the last statement, not yet cleared. NOT a break.
//   delta    → red    "Δ {n} vs bank"          — confirmed net (as of the last statement) diverges from the bank — a real break.
//   settled  → gray   "settled"                — delivered / expired / cascaded; reconciled in the ledger when live.
//   off_book → warning "not on bank"           — we hold a live position the latest statement doesn't list.
//   bilateral→ warning "BIL {n}"               — live bilateral-only inventory; expected to be absent from ECC.
// Slices (whole === false) of ties/pending/delta render MUTED since the slice net is partial — the
// status describes the WHOLE contract, so the tooltip is prefixed accordingly.
function BilateralValuationBadge({ node }: { node: GroupedNode }) {
  const incomplete = node.valuationIncomplete === true;
  const ronBranch = node.valuationCurrency === "RON";
  const lines = [
    node.dimension === "contract"
      ? "Bilateral RON reference valuation"
      : "Subtotal includes bilateral RON reference valuation",
  ];
  if (incomplete) {
    lines.push(`Incomplete: ${valuationReasonLabel(node.valuationIncompleteReason)}`);
    lines.push("MW remains visible; unavailable P&L is not replaced with zero.");
  } else {
    lines.push("Estimated/reference P&L; reporting EUR is shown in the P&L columns.");
  }
  if (node.nativeFixedPrice != null) lines.push(`Native fixed: ${fmtPx(node.nativeFixedPrice, 2)} RON/MWh`);
  if (node.nativeMarkPrice != null) lines.push(`Native mark: ${fmtPx(node.nativeMarkPrice, 2)} RON/MWh`);
  if (node.referenceMarkEUR != null) lines.push(`Reference mark: ${fmtPx(node.referenceMarkEUR, 2)} EUR/MWh`);
  if (node.nativePnl != null) lines.push(`Native P&L: ${fmtEur0(node.nativePnl)} RON`);
  if (node.totalEUR != null) lines.push(`Reporting P&L: ${fmtEur0(node.totalEUR)} EUR`);
  if (node.fxRate != null) {
    lines.push(`FX: EUR/RON ${fmtPx(node.fxRate, 4)} RON per EUR`);
    lines.push(`FX kind/date/source: ${node.fxKind ?? "—"} · ${node.fxDate ?? "—"} · ${node.fxSource ?? "—"}`);
  } else if (node.dimension !== "contract") {
    lines.push("FX may differ by delivery month; expand to the contract leaves for date, source and kind.");
  }

  return (
    <span
      title={lines.join("\n")}
      className={cn(
        "ml-1.5 rounded px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide sm:text-[10px] sm:font-normal",
        incomplete
          ? "bg-warnOrange/15 text-warnOrange"
          : "bg-accent/10 text-accent"
      )}
    >
      {incomplete ? (ronBranch ? "RON incomplete" : "valuation incomplete") : "RON est"}
    </span>
  );
}

function valuationReasonLabel(reason: ValuationIncompleteReason | null | undefined): string {
  const labels: Record<ValuationIncompleteReason, string> = {
    missing_reference_mark: "reference EUR mark is missing",
    missing_fx: "EUR/RON rate is missing",
    missing_reference_mark_and_fx: "reference EUR mark and EUR/RON rate are missing",
    missing_delivery_hours: "delivery hours are missing",
    unsupported_shape: "the delivery shape is not supported for this valuation",
    unsupported_settlement_index: "the settlement index is not supported for this valuation",
    child_incomplete: "one or more child positions are incomplete",
  };
  return reason ? labels[reason] : "required valuation inputs are missing";
}

function BankReconBadge({ recon }: { recon: BankRecon }) {
  const { state, contractNetMW, bilateralNetMW, bankNetMW, deltaMW, pendingMW, whole } = recon;
  const base = "ml-1.5 rounded px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide sm:text-[10px] sm:font-normal";
  const split = "ml-1.5 inline-flex overflow-hidden rounded align-middle text-xs font-medium uppercase tracking-wide sm:text-[10px] sm:font-normal";
  const seg = "px-1.5 py-0.5";
  const slicePrefix = !whole ? "whole contract: " : "";
  const sgn = (n: number) => (n > 0 ? `+${n}` : `${n}`);

  if (state === "bilateral") {
    const net = bilateralNetMW ?? 0;
    return (
      <span
        title={`${slicePrefix}${sgn(net)} MW bilateral position — internal exposure only; correctly absent from the ECC clearing statement and VM ledger`}
        className={cn(base, "bg-warnOrange/15 text-warnOrange sm:bg-amber-500/15 sm:text-amber-600 dark:sm:text-amber-400")}
      >
        BIL {sgn(net)}
      </span>
    );
  }

  if (state === "ties") {
    const flat = bankNetMW === null;
    const label = flat ? "✓ flat" : "✓ bank";
    const tip = flat
      ? "flat both sides"
      : `ties the bank's end-of-day net (your ${contractNetMW} = bank ${bankNetMW})`;
    // Ties/delta are slice-aware: a partial slice gets a muted outline pill.
    const tone = whole
      ? "bg-posGreen/15 text-posGreen"
      : "border border-posGreen/40 text-posGreen sm:text-posGreen/70";
    return (
      <span title={`${slicePrefix}${tip}`} className={cn(base, tone)}>
        {label}
      </span>
    );
  }

  if (state === "pending") {
    // Confirmed net ties the bank's last close; the rest is post-statement, not-yet-cleared trades.
    const bank = bankNetMW ?? 0;
    const pend = pendingMW ?? 0;
    const tone = whole ? "" : "border border-accent/40 sm:border-0 sm:opacity-60";
    return (
      <span
        title={`${slicePrefix}reconciles to the bank's last close (${sgn(bank)}); ${sgn(pend)} traded since — not on a statement yet, clears on the next one`}
        className={cn(split, tone)}
      >
        <span className={cn(seg, "bg-posGreen/15 text-posGreen")}>✓ {sgn(bank)} bank</span>
        <span className={cn(seg, "bg-accent/10 text-accent sm:bg-blue-500/15 sm:text-blue-600 dark:sm:text-blue-400")}>{sgn(pend)} uncleared</span>
      </span>
    );
  }

  if (state === "delta") {
    const d = deltaMW ?? 0;
    const confNet = (bankNetMW ?? 0) + d;
    const pendNote = pendingMW != null ? ` (plus ${sgn(pendingMW)} traded since, not yet cleared)` : "";
    const tone = whole
      ? "bg-negRed/15 text-negRed"
      : "border border-negRed/40 text-negRed sm:text-negRed/70";
    // A slice can itself happen to equal the bank while another book/trader
    // slice creates the contract-level break. Make that scope visible in the
    // pill itself, not only in its hover text.
    const label = whole ? `Δ ${sgn(d)} vs bank` : `contract Δ ${sgn(d)} vs bank`;
    return (
      <span
        title={`${slicePrefix}confirmed break — your net as of the last statement ${confNet} vs bank ${bankNetMW} (Δ ${sgn(d)})${pendNote}`}
        className={cn(base, tone)}
      >
        {label}
      </span>
    );
  }

  if (state === "settled") {
    // Contract-level fact — renders normally at any grain.
    return (
      <span
        title="delivered / expired — reconciled in the ledger when live; no current bank line"
        className={cn(base, "bg-border/30 text-fg sm:text-muted")}
      >
        settled
      </span>
    );
  }

  // off_book — also a contract-level fact; renders normally at any grain. The net
  // shown is the WHOLE contract's, so the tooltip is contract-level (no "here").
  return (
    <span
      title={`${slicePrefix}net ${contractNetMW} held, but the latest end-of-day statement doesn't list this contract`}
      className={cn(base, "bg-warnOrange/15 text-warnOrange sm:bg-amber-500/15 sm:text-amber-600 dark:sm:text-amber-400")}
    >
      not on bank
    </span>
  );
}

// Stable identity segment for a tree node, used to build a PERSISTABLE collapse-state
// path. An index-based path (slot in the array) shifts whenever the node set changes
// (a book added, a contract expires off the "active" view) and would silently
// forget or misapply the user's collapse choices. Keying on the node's own group
// value instead survives reloads and reordering. Origin sub-rows all share their
// parent leaf's (dimension, value) — see the GroupedNode.value comment — so they're
// disambiguated by cascade kind + origin instead.
function nodeKeySegment(node: GroupedNode, isOriginSubRow: boolean): string {
  if (isOriginSubRow) {
    const kind = node.cascadedIn ? "in" : node.cascadeOut ? "out" : "direct";
    return `sub:${kind}:${node.originLabel ?? ""}`;
  }
  // Terminal labels are not unique: the same contract can exist in several
  // provenance leaves, and Bilateral v2 intentionally creates a cleared and a
  // bilateral leaf with the same visible Pair/contract label. Include the full
  // economic leaf identity so React keys and persisted collapse paths cannot
  // alias one leaf onto another.
  if (node.dimension === "contract" && node.legs?.[0]) {
    const leg = node.legs[0];
    return `contract:${JSON.stringify([
      leg.code,
      leg.expYear,
      leg.expMonth,
      leg.bucket.book,
      leg.bucket.pair,
      leg.bucket.trader,
      leg.bucket.broker,
      leg.bucket.venue,
      leg.tradeOrigin ?? "cleared",
      leg.currency ?? null,
      leg.settlementIndex ?? null,
    ])}`;
  }
  return `${node.dimension}:${node.value}`;
}

function TreeTable({
  nodes,
  grouping,
  columns,
  priceDisplay,
  onReorderColumns,
  onResetColumns,
  columnsCustomized,
  onOpenLeaf,
  onOpenLeafRef,
  collapsed,
  onToggleCollapsed,
}: {
  nodes: GroupedNode[];
  // The active grouping order — threaded to leaves so LeafAttrChips can surface
  // provenance attributes (e.g. `pair`) that are NOT a grouping level.
  grouping: GroupDimension[];
  // Metric columns in the user's chosen order (Group is pinned, not in here).
  columns: MetricCol[];
  priceDisplay: PriceDisplayContext;
  onReorderColumns: (fromId: MetricColId, toId: MetricColId) => void;
  onResetColumns: () => void;
  columnsCustomized: boolean;
  onOpenLeaf: (node: GroupedNode) => void;
  onOpenLeafRef: (
    ref: { code: string; expYear: number; expMonth: number },
    bucket: ProvenanceBucket,
    tradeOrigin: TradeOrigin,
    currency?: "EUR" | "GBP" | "RON" | null,
    settlementIndex?: string | null
  ) => void;
  // Which group paths are collapsed — owned by the page and persisted per user
  // alongside the other toolbar prefs (see TreePrefs).
  collapsed: Set<string>;
  onToggleCollapsed: (path: string) => void;
}) {
  // Transient drag state for header reordering: the column being dragged and the
  // one it's hovering over. On drop the dragged column lands next to the target —
  // AFTER it when dragging rightward, BEFORE it when leftward (see reorderColumnIds).
  const [dragId, setDragId] = useState<MetricColId | null>(null);
  const [overId, setOverId] = useState<MetricColId | null>(null);
  const endDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  if (nodes.length === 0) {
    return <div className="text-muted text-sm">No positions for this grouping.</div>;
  }

  return (
    <div className="min-w-0 max-w-full space-y-1">
      {columnsCustomized && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onResetColumns}
            title="Restore the default column order"
            className="inline-flex min-h-11 touch-manipulation items-center text-sm font-medium text-fg underline decoration-dotted underline-offset-2 outline-none hover:text-accent focus-visible:ring-2 focus-visible:ring-accent sm:min-h-0 sm:text-[10px] sm:font-normal sm:text-muted sm:hover:text-fg"
          >
            ↺ Reset column order
          </button>
        </div>
      )}
      <p className="text-xs text-muted sm:hidden">Swipe within the table to review every metric. The group column stays pinned.</p>
      <div className="ops-table-frame max-h-[65dvh] max-w-full overscroll-contain rounded-md border border-border/60 overflow-auto sm:max-h-[calc(100vh-16rem)] sm:max-w-none sm:overscroll-auto">
        <table className="w-max min-w-full text-xs sm:w-auto">
          <thead className="bg-bg sticky top-0 text-muted">
            <tr>
              <Th className="sticky left-0 z-20 min-w-[13rem] bg-bg sm:static sm:z-auto sm:min-w-0 sm:bg-transparent">Group</Th>
              {/* Metric headers use a raw <th> (not the shared <Th>) because they carry
                  DnD handlers + dynamic drag classes <Th> can't forward; the base classes
                  below mirror <Th> in components/eex/ui. Reorderable by drag AND by keyboard
                  (focus a header, ◀/▶ to move) — matching the app's keyboard-operable
                  precedent (GroupingBar's ↑/↓, the keyboard-activatable drill rows). */}
              {columns.map((col, idx) => (
                <th
                  key={col.id}
                  scope="col"
                  draggable={!col.static}
                  tabIndex={col.static ? -1 : 0}
                  aria-label={
                    col.static
                      ? `${col.label} (month column)`
                      : `${col.label}, column ${idx + 1} of ${columns.length}. Press left or right arrow to move it, or drag to reorder.`
                  }
                  onDragStart={(e) => {
                    if (col.static) return;
                    setDragId(col.id as MetricColId);
                    e.dataTransfer.effectAllowed = "move";
                    // Firefox only initiates a drag if some data is set.
                    e.dataTransfer.setData("text/plain", col.id);
                  }}
                  onDragOver={(e) => {
                    // Only accept the drop while OUR column drag is in flight — otherwise a
                    // foreign drag (page text, a file) would show a false "move" cursor here.
                    if (!dragId) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDragEnter={() => {
                    if (dragId && dragId !== col.id && !col.static) setOverId(col.id as MetricColId);
                  }}
                  onDragLeave={() => {
                    // Drop the hover highlight when the pointer leaves THIS column (e.g. onto
                    // the pinned Group header or outside the table). Guarded so it doesn't
                    // undo the next header's dragEnter, which fires first.
                    setOverId((cur) => (cur === col.id ? null : cur));
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId && dragId !== col.id && !col.static)
                      onReorderColumns(dragId as MetricColId, col.id as MetricColId);
                    endDrag();
                  }}
                  onDragEnd={endDrag}
                  onKeyDown={(e) => {
                    if (col.static) return;
                    const dir = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
                    if (!dir) return;
                    const neighbor = columns[idx + dir];
                    if (!neighbor || neighbor.static) return; // edge / month zone — no move
                    e.preventDefault();
                    onReorderColumns(col.id as MetricColId, neighbor.id as MetricColId);
                  }}
                  title={col.static ? col.label : `${col.label} — drag, or focus and press ◀ / ▶, to reorder`}
                  className={cn(
                    "select-none whitespace-nowrap px-2 py-3 text-right font-normal sm:py-1.5",
                    col.static ? "cursor-default" : "cursor-grab active:cursor-grabbing",
                    "focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent",
                    dragId === col.id && "opacity-40",
                    overId === col.id && dragId !== col.id && "bg-accent/20 text-fg"
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map((n, index) => {
              const segment = `${nodeKeySegment(n, false)}#${index}`;
              return (
              <TreeRows
                key={segment}
                node={n}
                depth={0}
                path={segment}
                collapsed={collapsed}
                grouping={grouping}
                columns={columns}
                priceDisplay={priceDisplay}
                onToggle={onToggleCollapsed}
                onOpenLeaf={onOpenLeaf}
                onOpenLeafRef={onOpenLeafRef}
              />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TreeRows({
  node,
  depth,
  path,
  collapsed,
  grouping,
  columns,
  priceDisplay,
  onToggle,
  onOpenLeaf,
  onOpenLeafRef,
  isOriginSubRow = false,
}: {
  node: GroupedNode;
  depth: number;
  path: string;
  collapsed: Set<string>;
  // Active grouping order — used by LeafAttrChips on the leaf row.
  grouping: GroupDimension[];
  // Metric columns in the user's chosen order — the same list the header renders.
  columns: MetricCol[];
  priceDisplay: PriceDisplayContext;
  onToggle: (key: string) => void;
  onOpenLeaf: (node: GroupedNode) => void;
  onOpenLeafRef: (
    ref: { code: string; expYear: number; expMonth: number },
    bucket: ProvenanceBucket,
    tradeOrigin: TradeOrigin,
    currency?: "EUR" | "GBP" | "RON" | null,
    settlementIndex?: string | null
  ) => void;
  // True when this node is a per-lineage breakdown row under a mixed-lineage
  // contract leaf. Sub-rows are DISPLAY-only: net MW + gross + lineage tag, with
  // dashed P&L (realized/mtm/total are null on them). They are NOT a drill target —
  // the PARENT contract row is. Clearing origin remains fixed across these rows.
  isOriginSubRow?: boolean;
}) {
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(path);
  const isLeaf = node.dimension === "contract";
  // A contract leaf is drill-able only when it actually carries fills — and never
  // a sub-row (its parent leaf is the click target).
  const canDrill = isLeaf && !isOriginSubRow && !!node.legs && node.legs.length > 0;
  // A "← from X" lineage tag drills into X's own (bucket-scoped) leaf. Only the
  // cascaded-in case is a link (a "traded as <self>" tag points at this same leaf).
  const lineageDrill =
    node.cascadedIn && node.originContract && node.legs && node.legs[0]
      ? () =>
          onOpenLeafRef(
            node.originContract!,
            node.legs![0]!.bucket,
            node.legs![0]!.tradeOrigin ?? "cleared",
            node.legs![0]!.currency ?? null,
            node.legs![0]!.settlementIndex ?? null
          )
      : undefined;

  return (
    <Fragment>
      <tr
        className={cn(
          "group border-t border-border/30 hover:bg-border/10",
          !isLeaf && "ops-position-group",
          isLeaf && !isOriginSubRow && "bg-border/5",
          // Origin sub-rows read as a muted breakdown beneath the leaf, not as a
          // standalone leaf — lighter background, no drill affordance.
          isOriginSubRow && "bg-transparent text-muted",
          canDrill && "cursor-pointer outline-none hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
        )}
        {...(canDrill
          ? {
              onClick: () => onOpenLeaf(node),
            }
          : {})}
      >
        <Td className="sticky left-0 z-10 min-w-[13rem] max-w-[18rem] whitespace-normal bg-bg align-top sm:static sm:z-auto sm:min-w-0 sm:max-w-none sm:whitespace-nowrap sm:bg-transparent sm:align-baseline">
          <div style={{ paddingLeft: depth * 16 }} className="flex min-w-0 flex-wrap items-center gap-1 sm:flex-nowrap">
            {hasChildren ? (
              <button
                type="button"
                aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${node.value}`}
                aria-expanded={!isCollapsed}
                onKeyDown={(event) => event.stopPropagation()}
                onClick={(e) => {
                  // On a drillable mixed-lineage leaf the row itself opens the
                  // drill-down; stop the toggle click from bubbling into it so the
                  // chevron only collapses/expands the origin breakdown.
                  e.stopPropagation();
                  onToggle(path);
                }}
                className="-my-2 inline-flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded text-muted outline-none hover:bg-border/20 hover:text-fg focus-visible:ring-2 focus-visible:ring-accent sm:my-0 sm:inline-block sm:h-auto sm:w-4 sm:rounded-none sm:hover:bg-transparent"
              >
                {isCollapsed ? "▸" : "▾"}
              </button>
            ) : (
              <span className="h-11 w-11 shrink-0 sm:h-auto sm:w-4" />
            )}
            {isOriginSubRow ? (
              // The breakdown row doesn't repeat the contract label — the slice tag
              // IS the label. A "↳" leader marks it as a child of the leaf. One of
              // three kinds: cascaded-IN (← from X, drillable), the SELF-CLOSE
              // (↻ cascade close), or directly-traded fills.
              <>
                <span className="shrink-0 text-fg sm:text-muted/70">↳</span>
                {node.cascadedIn && node.originLabel != null ? (
                  <LineageTag originLabel={node.originLabel} onDrill={lineageDrill} />
                ) : node.cascadeOut ? (
                  <CascadeCloseTag to={node.rollToLabel} />
                ) : (
                  <DirectFillsTag />
                )}
              </>
            ) : (
              <>
                {canDrill ? (
                  <button
                    type="button"
                    aria-label={`Drill into ${node.value}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenLeaf(node);
                    }}
                    className={cn(
                      "inline-flex min-h-11 items-center rounded py-2 text-left outline-none hover:underline focus-visible:ring-2 focus-visible:ring-accent sm:min-h-0 sm:py-0",
                      isLeaf ? "font-mono" : "font-semibold"
                    )}
                  >
                    {node.value}
                  </button>
                ) : (
                  <span className={cn(isLeaf ? "font-mono" : "font-semibold")}>
                    {node.value}
                  </span>
                )}
                {isLeaf && (
                  <TradeOriginChip origin={node.legs?.[0]?.tradeOrigin ?? "cleared"} />
                )}
                {isLeaf && (
                  <LeafAttrChips bucket={node.legs?.[0]?.bucket} grouping={grouping} />
                )}
                {isLeaf && node.cascadedIn && node.originLabel != null && (
                  <LineageTag originLabel={node.originLabel} onDrill={lineageDrill} />
                )}
                {isLeaf && (
                  <LifecycleTag
                    lifecycle={node.lifecycle}
                    deliveredFraction={node.deliveredFraction}
                    netMW={node.netMW}
                  />
                )}
                {(node.valuationIncomplete !== undefined || node.valuationCurrency === "RON") && (
                  <BilateralValuationBadge node={node} />
                )}
                {node.bankRecon != null && (
                  <>
                    <BankReconBadge recon={node.bankRecon} />
                    {node.bankRecon.state !== "bilateral" && node.bankRecon.bilateralNetMW != null && (
                      <span
                        title={`${node.bankRecon.bilateralNetMW > 0 ? "+" : ""}${fmtQty(node.bankRecon.bilateralNetMW)} MW bilateral offset — shown in the internal position, excluded from the ECC bank tie and VM ledger`}
                        className="ml-1.5 rounded bg-warnOrange/15 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-warnOrange sm:bg-amber-500/15 sm:text-[10px] sm:font-normal sm:text-amber-600 dark:sm:text-amber-400"
                      >
                        BIL {node.bankRecon.bilateralNetMW > 0 ? "+" : ""}{fmtQty(node.bankRecon.bilateralNetMW)}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Td>
        {columns.map((col) => (
          <Fragment key={col.id}>{col.render(node, priceDisplay)}</Fragment>
        ))}
      </tr>
      {hasChildren &&
        !isCollapsed &&
        node.children.map((child, childIndex) => {
          const childPath = `${path}/${nodeKeySegment(child, isLeaf)}#${childIndex}`;
          return (
            <TreeRows
              key={childPath}
              node={child}
              depth={depth + 1}
              path={childPath}
              collapsed={collapsed}
              grouping={grouping}
              columns={columns}
              priceDisplay={priceDisplay}
              onToggle={onToggle}
              onOpenLeaf={onOpenLeaf}
              onOpenLeafRef={onOpenLeafRef}
              // Children of a contract leaf are its cascade-lineage breakdown
              // (display-only sub-rows); children of roll-ups are ordinary groups.
              isOriginSubRow={isLeaf}
            />
          );
        })}
    </Fragment>
  );
}

// ── Leaf drill-down modal ─────────────────────────────────────────────────────
// Fetches the read-only POST /eex/positions/tree/leaf and renders only the reply —
// no P&L / FIFO is recomputed here. Closes on backdrop click and Escape, traps
// focus, and is announced as a dialog. Portaled to document.body so the page's
// overflow-clipped tree container can't crop it.
const LEAF_DIALOG_HISTORY_KEY = "__cascenaEexPositionsLeafDialog";

function isLeafDialogHistoryEntry(): boolean {
  const state = window.history.state as Record<string, unknown> | null;
  return state?.[LEAF_DIALOG_HISTORY_KEY] === true;
}

function LeafModal({
  req,
  onClose,
  onNavigate,
}: {
  req: LeafRequest;
  onClose: () => void;
  onNavigate: (
    ref: { code: string; expYear: number; expMonth: number },
    bucket: ProvenanceBucket,
    tradeOrigin: TradeOrigin,
    currency?: "EUR" | "GBP" | "RON" | null,
    settlementIndex?: string | null
  ) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (isLeafDialogHistoryEntry()) {
      window.history.back();
      return;
    }
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!isLeafDialogHistoryEntry()) {
      const state = window.history.state;
      window.history.pushState(
        {
          ...(state && typeof state === "object" ? state : {}),
          [LEAF_DIALOG_HISTORY_KEY]: true,
        },
        ""
      );
    }

    const onPopState = () => {
      if (isLeafDialogHistoryEntry()) return;
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const leafQ = useQuery({
    // Ref + every bucket field + clearing origin key the cache. Cascade lineage
    // is deliberately absent; it resolves within the same economic leaf.
    queryKey: positionsTreeQueryKeys.leaf(req),
    queryFn: async () => {
      const result = await api<LeafExplanation>("/eex/positions/tree/leaf", {
        method: "POST",
        body: JSON.stringify(req),
      });
      if ((result.fxBasis ?? "forward") !== (req.fxBasis ?? "forward")) {
        throw new Error("The server returned a different FX valuation basis. Close and reopen this position to retry.");
      }
      return result;
    },
    staleTime: 30_000,
  });

  // Escape closes; focus the dialog on mount; restore focus on unmount; lock
  // background scroll while open.
  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [requestClose]);

  // Minimal focus trap: cycle Tab within the dialog.
  const onTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (active === root) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const data = leafQ.data;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/50 p-2 sm:items-start sm:overflow-y-auto sm:p-8"
      onClick={requestClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="leaf-dialog-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onTrapKeyDown}
        className="operations-workspace ops-dialog my-auto flex min-h-0 max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col rounded-lg border border-border/60 bg-bg text-fg shadow-2xl outline-none sm:block sm:min-h-[auto] sm:max-h-none"
      >
        <div className="flex items-start gap-3 border-b border-border/60 px-4 py-3">
          <div className="min-w-0 flex-1">
            {data ? (
              <LeafHeader data={data} tradeOrigin={req.tradeOrigin} />
            ) : (
              <h2 id="leaf-dialog-title" className="text-sm font-semibold">
                Contract drill-down
              </h2>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="inline-flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded text-muted outline-none hover:bg-border/30 hover:text-fg focus-visible:ring-2 focus-visible:ring-accent sm:h-auto sm:w-auto sm:px-2 sm:py-0.5"
          >
            ✕
          </button>
        </div>

        <div
          role="region"
          aria-label="Contract drill-down details"
          tabIndex={0}
          className="min-w-0 flex-1 space-y-5 overflow-x-hidden overflow-y-auto px-3 py-3 text-xs sm:overflow-visible sm:px-4"
        >
          {leafQ.isLoading && <div className="text-muted">Loading drill-down…</div>}
          {leafQ.isError && (
            <div className="text-negRed">{errMessage(leafQ.error)}</div>
          )}
          {data && (
            <LeafBody
              data={data}
              onNavigate={(ref) =>
                onNavigate(
                  ref,
                  req.bucket,
                  req.tradeOrigin,
                  req.currency,
                  req.settlementIndex
                )
              }
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function BucketChips({ bucket }: { bucket: ProvenanceBucket }) {
  const entries: { label: string; value: string }[] = [
    { label: "book", value: bucket.book ?? "" },
    { label: "pair", value: bucket.pair ?? "" },
    { label: "trader", value: bucket.trader ?? "" },
    { label: "broker", value: bucket.broker ?? "" },
    { label: "venue", value: bucket.venue ?? "" },
  ].filter((e) => e.value !== "");
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {entries.map((e) => (
        <span
          key={e.label}
          className="rounded border border-border/60 px-1.5 py-0.5 text-xs font-medium text-fg sm:text-[11px] sm:font-normal sm:text-muted"
        >
          <span className="font-semibold uppercase tracking-wide text-accent sm:font-normal sm:text-inherit sm:opacity-70">{e.label}</span>{" "}
          <span className="text-fg">{e.value}</span>
        </span>
      ))}
    </div>
  );
}

function LeafHeader({
  data,
  tradeOrigin,
}: {
  data: LeafExplanation;
  tradeOrigin: TradeOrigin;
}) {
  const ron = data.valuation?.valuationCurrency === "RON" ? data.valuation : null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
        <h2 id="leaf-dialog-title" className="text-sm font-semibold">
          {data.contract.label}
        </h2>
        <TradeOriginChip origin={tradeOrigin} />
        {ron && (
          <span className="rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-accent sm:text-[10px] sm:font-normal">
            RON reference
          </span>
        )}
        <LifecycleTag
          lifecycle={data.lifecycle}
          deliveredFraction={data.deliveredFraction}
          netMW={data.gross.net}
        />
      </div>
      <BucketChips bucket={data.bucket} />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg sm:text-[11px] sm:text-muted">
        <span>
          {ron ? "native mark " : "marked at "}
          <span className="tabular-nums text-fg">
            {ron
              ? ron.nativeMarkPrice != null
                ? `${fmtPx(ron.nativeMarkPrice, 2)} RON/MWh`
                : "—"
              : data.mark != null
                ? fmtPx(data.mark, 2)
                : "—"}
          </span>
        </span>
        {ron?.referenceMarkEUR != null && (
          <span>
            {ron.referenceMethod === "exact_contract" ? "contract settlement " : (ron.components?.length ?? 0) > 1 ? "weighted reference " : "reference "}
            <span className="tabular-nums text-fg">
              {fmtPx(ron.referenceMarkEUR, 2)} EUR/MWh
            </span>
          </span>
        )}
        {ron?.fxRate != null && (
          <span>
            EUR/RON{" "}
            <span className="tabular-nums text-fg">{fmtPx(ron.fxRate, 4)}</span>
          </span>
        )}
        <span>
          <span className="tabular-nums text-fg">
            {data.mwhStd != null ? fmtQty(data.mwhStd) : "—"}
          </span>{" "}
          h
        </span>
        {ron ? (
          <span>OPCOM reference valuation · outside Recognition</span>
        ) : (
          <span>
            {METHOD_LABELS[data.method]}
            <span className="text-fg sm:text-inherit sm:opacity-70"> — book default</span>
          </span>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-fg sm:text-[11px] sm:text-muted">
      {children}
    </div>
  );
}

// B/S badge in the page's posGreen/negRed convention (buy long = green).
function SideTag({ side }: { side: "B" | "S" }) {
  return (
    <span
      className={cn(
        "inline-block w-4 text-center font-semibold",
        side === "B" ? "text-posGreen" : "text-negRed"
      )}
    >
      {side}
    </span>
  );
}

// #3 Tiny accent chip marking a matching-ladder leg as a cascade (non-trade) leg —
// the exchange closed/rolled the position at final settlement, not a real fill.
function CascRungChip() {
  return (
    <span
      title="this leg is a cascade (non-trade) leg — the exchange closed/rolled the position at final settlement on the last trading day"
      className="rounded bg-accent/10 px-1 py-px text-xs font-medium uppercase tracking-wide text-accent sm:text-[9px] sm:font-normal"
    >
      casc
    </span>
  );
}

// A "buy N @ vwap · sell N @ vwap · net N" summary line for one side-aggregate.
// Used both for the original single-line fills footer (breakdown == null, byte-
// identical to before) and for the real/cascade breakdown lines. `accent` shows
// the net in accent (the cascade line carries its own arrow to the whole net, so
// it suppresses its own net colouring) — the default reproduces the prior colours.
function SideAggLine({ agg, accent }: { agg: SideAgg; accent?: boolean }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
      <span>
        <span className="text-posGreen">buy</span>{" "}
        <span className="tabular-nums text-fg">{agg.buyLots}</span>
        {agg.buyVwap != null && (
          <>
            {" @ "}
            <span className="tabular-nums text-fg">{fmtPx(agg.buyVwap, 2)}</span>
          </>
        )}
      </span>
      <span>
        <span className="text-negRed">sell</span>{" "}
        <span className="tabular-nums text-fg">{agg.sellLots}</span>
        {agg.sellVwap != null && (
          <>
            {" @ "}
            <span className="tabular-nums text-fg">{fmtPx(agg.sellVwap, 2)}</span>
          </>
        )}
      </span>
      {!accent && (
        <span>
          net{" "}
          <span
            className={cn(
              "tabular-nums",
              agg.net > 0 ? "text-posGreen" : agg.net < 0 ? "text-negRed" : "text-fg"
            )}
          >
            {agg.net > 0 ? `+${agg.net}` : agg.net}
          </span>
        </span>
      )}
    </div>
  );
}

function LeafBody({
  data,
  onNavigate,
}: {
  data: LeafExplanation;
  // Jump the drill-down to a cascaded-IN fill's parent contract (same bucket). When
  // omitted, the cascaded-in chip renders exactly as before (no link, no chevron).
  onNavigate?: (ref: { code: string; expYear: number; expMonth: number }) => void;
}) {
  if (data.valuation?.valuationCurrency === "RON") {
    return <RonBilateralLeafBody data={data} valuation={data.valuation} />;
  }
  const { gross, ladder, residual } = data;
  const wa = data.method === "weighted_average";
  const phi = data.deliveredFraction;
  const phiPct = phi != null ? Math.round(phi * 100) : null;
  // deliveredFraction is net-blind (contract-calendar driven), so a flat (net-0)
  // round-trip leaf still carries phi>0. A closed position has no open residual
  // for delivery to fold into realized (applyLifecycle is a no-op when mtmEUR is
  // null), so the "% delivered" note is a false delivery claim there — gate it on
  // net, mirroring the LifecycleTag netMW===0 suppression.
  const showPhiNote = gross.net !== 0 && phi != null && phi > 0 && phi <= 1;

  return (
    <Fragment>
      {/* Fills */}
      <section className="space-y-1.5">
        <SectionTitle>Fills</SectionTitle>
        <div className="overflow-x-auto rounded border border-border/40">
          <table className="min-w-full text-xs">
            <thead className="text-muted">
              <tr className="border-b border-border/40">
                <Th>Date</Th>
                <Th>Side</Th>
                <Th className="text-right">Lots</Th>
                <Th className="text-right">Price</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {data.fills.map((f, i) => {
                // #1 Cascade legs are NOT trades — the exchange closed/rolled the
                // position at final settlement on the last trading day. Tint the row,
                // add a left accent border, mute the numeric text, and swap the plain
                // grey badge for an adaptive accent chip. Non-cascade rows are unchanged.
                const chipLabel =
                  f.cascadeKind === "close"
                    ? "cascade close"
                    : "cascaded in" + (f.originLabel ? " from " + f.originLabel : "");
                const cascadeTitle =
                  "not a trade — the exchange closed/rolled this position at final settlement on the last trading day" +
                  (f.cascadeKind === "in" && f.originLabel
                    ? ` (cascaded in from ${f.originLabel})`
                    : f.cascadeKind === "close"
                      ? " (this contract closing/rolling itself out)"
                      : "");
                return (
                  <tr
                    key={i}
                    className={cn(
                      "border-t border-border/20",
                      f.cascaded && "bg-accent/5 border-l-2 border-accent/60"
                    )}
                  >
                    <Td className={cn("font-mono", f.cascaded && "text-muted")}>{f.date}</Td>
                    <Td className={cn(f.cascaded && "sm:opacity-60")}>
                      <SideTag side={f.side} />
                    </Td>
                    <Td className={cn("text-right tabular-nums", f.cascaded && "text-muted")}>
                      {f.lots}
                    </Td>
                    <Td className={cn("text-right tabular-nums", f.cascaded && "text-muted")}>
                      {fmtPx(f.price, 2)}
                    </Td>
                    <Td>
                      {f.cascaded &&
                        (f.cascadeKind === "in" && f.originContract && onNavigate ? (
                          // A cascaded-IN fill with a navigable parent: render the chip
                          // as a clickable control that jumps the drill-down to X (same
                          // bucket). Recursion is automatic — X's own modal re-renders
                          // this same body with its own cascaded-in fills clickable.
                          <span
                            role="button"
                            tabIndex={0}
                            title={`click to open ${f.originLabel}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigate(f.originContract!);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                onNavigate(f.originContract!);
                              }
                            }}
                            className="inline-flex min-h-11 cursor-pointer touch-manipulation items-center gap-0.5 rounded bg-accent/10 px-2 py-1 text-xs font-medium tracking-wide text-accent outline-none hover:bg-accent/20 focus-visible:ring-2 focus-visible:ring-accent sm:min-h-0 sm:px-1.5 sm:py-0.5 sm:text-[11px] sm:font-normal"
                          >
                            {chipLabel}
                            <span aria-hidden="true">›</span>
                          </span>
                        ) : (
                          <span
                            title={cascadeTitle}
                            className="rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium tracking-wide text-accent sm:text-[11px] sm:font-normal"
                          >
                            {chipLabel}
                          </span>
                        ))}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {/* #2 When the leaf has cascade legs, split the footer into a REAL line
                  (the desk's true net, e.g. −1) and a CASCADE line that arrows to the
                  post-cascade whole net (gross.net, e.g. 0). When there are no cascade
                  legs (breakdown == null), keep the EXISTING single line unchanged. */}
              {data.breakdown == null ? (
                <tr className="border-t border-border/60 font-semibold text-muted">
                  <Td colSpan={5}>
                    <SideAggLine agg={gross} />
                  </Td>
                </tr>
              ) : (
                <>
                  <tr className="border-t border-border/60 font-semibold text-muted">
                    <Td colSpan={5}>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[10px] sm:font-normal sm:text-muted">
                          real
                        </span>
                        <SideAggLine agg={data.breakdown.real} />
                      </div>
                    </Td>
                  </tr>
                  <tr className="font-semibold text-accent">
                    <Td colSpan={5}>
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-accent sm:text-[10px] sm:font-normal">
                          cascade
                        </span>
                        <SideAggLine agg={data.breakdown.cascade} accent />
                        <span className="text-fg sm:text-muted">→ net</span>
                        <span
                          className={cn(
                            "tabular-nums",
                            gross.net > 0
                              ? "text-posGreen"
                              : gross.net < 0
                                ? "text-negRed"
                                : "text-fg"
                          )}
                        >
                          {gross.net > 0 ? `+${gross.net}` : gross.net}
                        </span>
                      </div>
                    </Td>
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      </section>

      {/* Matching ladder */}
      <section className="space-y-1.5">
        <SectionTitle>Matching ladder</SectionTitle>
        {ladder.length === 0 ? (
          <div className="rounded border border-border/40 px-3 py-2 text-fg sm:text-muted">
            {wa
              ? "weighted-average — no explicit pairs (gross netting)"
              : "no matched pairs"}
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-border/40">
            <table className="min-w-full text-xs">
              <thead className="text-muted">
                <tr className="border-b border-border/40">
                  <Th className="text-right">Buy</Th>
                  <Th className="text-right">Sell</Th>
                  <Th className="text-right">Lots</Th>
                  <Th className="text-right">€/MWh</Th>
                  <Th>Close date</Th>
                </tr>
              </thead>
              <tbody>
                {ladder.map((r, i) => (
                  <tr
                    key={i}
                    className={cn(
                      "border-t border-border/20",
                      // #3 Faint tint when either leg of the rung is a cascade leg.
                      (r.buyCascaded || r.sellCascaded) && "bg-accent/5"
                    )}
                  >
                    <Td className="text-right tabular-nums">
                      <span className="inline-flex items-center justify-end gap-1">
                        {fmtPx(r.buy, 2)}
                        {r.buyCascaded && <CascRungChip />}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">
                      <span className="inline-flex items-center justify-end gap-1">
                        {fmtPx(r.sell, 2)}
                        {r.sellCascaded && <CascRungChip />}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">{r.lots}</Td>
                    <Td
                      className={cn(
                        "text-right tabular-nums",
                        r.perMWh > 0
                          ? "text-posGreen"
                          : r.perMWh < 0
                            ? "text-negRed"
                            : ""
                      )}
                    >
                      {fmtPx(r.perMWh, 2)}
                    </Td>
                    <Td className="font-mono">{r.closeDate}</Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border/60 font-semibold">
                  <Td colSpan={5} className="text-right">
                    Realized{" "}
                    <span
                      className={cn(
                        "tabular-nums",
                        data.tradingRealizedEUR != null && data.tradingRealizedEUR > 0
                          ? "text-posGreen"
                          : data.tradingRealizedEUR != null &&
                              data.tradingRealizedEUR < 0
                            ? "text-negRed"
                            : ""
                      )}
                    >
                      {fmtEur0(data.tradingRealizedEUR)} €
                    </span>
                  </Td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="text-xs text-fg sm:text-[11px] sm:text-muted">
          {data.closedLots} lot(s) closed.
        </div>
      </section>

      {/* Residual */}
      <section className="space-y-1.5">
        <SectionTitle>Residual</SectionTitle>
        <div className="rounded border border-border/40 px-3 py-2">
          {residual && residual.side ? (
            <div className="flex flex-wrap items-center gap-x-1">
              <span className="tabular-nums font-semibold">{residual.lots}</span>
              <span
                className={cn(
                  "font-semibold uppercase tracking-wide",
                  residual.side === "B" ? "text-posGreen" : "text-negRed"
                )}
              >
                {residual.side === "B" ? "long" : "short"}
              </span>
              <span className="text-fg sm:text-muted">open</span>
              {residual.avgPrice != null && (
                <>
                  <span className="text-fg sm:text-muted">@</span>
                  <span className="tabular-nums">{fmtPx(residual.avgPrice, 3)}</span>
                </>
              )}
              <span className="text-fg sm:text-muted">, marked to</span>
              <span className="tabular-nums">
                {data.mark != null ? fmtPx(data.mark, 2) : "—"}
              </span>
              <span className="text-fg sm:text-muted">→ MtM</span>
              <span
                className={cn(
                  "tabular-nums font-semibold",
                  data.mtmEUR != null && data.mtmEUR > 0
                    ? "text-posGreen"
                    : data.mtmEUR != null && data.mtmEUR < 0
                      ? "text-negRed"
                      : ""
                )}
              >
                {fmtEur0(data.mtmEUR)} €
              </span>
            </div>
          ) : (
            <span className="text-fg sm:text-muted">flat — no open residual</span>
          )}
        </div>
      </section>

      {/* Valuation */}
      <section className="space-y-1.5">
        <SectionTitle>Valuation</SectionTitle>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <ValueCell label="Realized" value={data.realizedEUR} />
          <ValueCell label="MtM" value={data.mtmEUR} />
          <ValueCell label="Total" value={data.totalEUR} emphasize />
        </div>
        {showPhiNote && (
          <div className="text-xs text-fg sm:text-[11px] sm:text-muted">
            {phiPct}% delivered → that share folded into realized.
          </div>
        )}
      </section>
    </Fragment>
  );
}

function RonBilateralLeafBody({
  data,
  valuation,
}: {
  data: LeafExplanation;
  valuation: NonNullable<LeafExplanation["valuation"]>;
}) {
  const incomplete = valuation.valuationIncomplete;
  const components = valuation.components ?? [];
  const wholeDeal = components.length > 1;
  const referenceEquation =
    valuation.referenceMarkEUR != null && valuation.fxRate != null
      ? `${fmtPx(valuation.referenceMarkEUR, 2)} EUR/MWh × ${fmtPx(valuation.fxRate, 4)} = ${
          valuation.nativeMarkPrice != null ? fmtPx(valuation.nativeMarkPrice, 2) : "—"
        } RON/MWh`
      : `Incomplete: ${valuationReasonLabel(valuation.valuationIncompleteReason)}.`;

  return (
    <Fragment>
      <section className="space-y-1.5">
        <SectionTitle>Contractual fixed exposures</SectionTitle>
        <div className="overflow-x-auto rounded border border-border/40">
          <table className="min-w-full text-xs">
            <thead className="text-muted">
              <tr className="border-b border-border/40">
                <Th>Date</Th>
                <Th>Cashflow direction</Th>
                <Th className="text-right">MW</Th>
                <Th className="text-right">Fixed price (RON/MWh)</Th>
              </tr>
            </thead>
            <tbody>
              {data.fills.map((fill, index) => (
                <tr key={index} className="border-t border-border/20">
                  <Td className="font-mono">{fill.date}</Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      <SideTag side={fill.side} />
                      <span>
                        {fill.side === "B"
                          ? "pay fixed / receive floating"
                          : "receive fixed / pay floating"}
                      </span>
                    </span>
                  </Td>
                  <Td className="text-right tabular-nums">{fill.lots}</Td>
                  <Td className="text-right tabular-nums">{fmtPx(fill.price, 2)}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border/60 font-semibold text-muted">
                <Td colSpan={4}>
                  net{" "}
                  <span
                    className={cn(
                      "tabular-nums",
                      data.gross.net > 0
                        ? "text-posGreen"
                        : data.gross.net < 0
                          ? "text-negRed"
                          : "text-fg"
                    )}
                  >
                    {data.gross.net > 0 ? `+${data.gross.net}` : data.gross.net} MW
                  </span>{" "}
                  · {data.settlementIndex ?? "unknown index"}
                </Td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="space-y-1.5">
        <SectionTitle>Reference valuation</SectionTitle>
        <div
          className={cn(
            "rounded border px-3 py-2",
            incomplete
              ? "border-warnOrange/50 bg-warnOrange/5"
              : "border-accent/40 bg-accent/5"
          )}
        >
          <div className="font-mono tabular-nums">
            {wholeDeal
              ? `${valuation.referenceMethod === "exact_contract" ? "Contract settlement" : "Weighted reference"}: ${valuation.referenceMarkEUR == null ? "unavailable" : `${fmtPx(valuation.referenceMarkEUR, 2)} EUR/MWh`}${valuation.referenceMethod === "exact_contract" && valuation.referenceDate ? ` · ${valuation.referenceDate}` : ""}. Reporting estimate sums the individual period valuations below.`
              : referenceEquation}
          </div>
          {valuation.referenceMethod === "exact_contract" && (
            <div className="mt-1 text-fg sm:text-muted">
              {valuation.referenceAllocation === "flat_contract"
                ? "The complete monthly price curve is unavailable, so the contract settlement is allocated at the same EUR/MWh price to each period."
                : "Monthly price differences are preserved and adjusted equally in EUR/MWh so their hours-weighted average matches the contract settlement."}
              {" "}Each period retains its own FX rate. Allocated prices below are valuation references, not individual monthly settlements.
            </div>
          )}
          {incomplete ? (
            <div className="mt-1 text-warnOrange">
              Incomplete: {valuationReasonLabel(valuation.valuationIncompleteReason)}. MW and
              fixed terms remain visible; P&amp;L is not replaced with zero.
            </div>
          ) : !wholeDeal ? (
            <div className="mt-1 text-fg sm:text-muted">
              FX {valuation.fxKind ?? "—"} · {valuation.fxDate ?? "—"} · {valuation.fxSource ?? "—"}
            </div>
          ) : null}
        </div>
        {components.length > 0 && (
          <div className="overflow-x-auto rounded border border-border/40">
            <table className="w-full min-w-[660px] table-fixed text-xs" aria-label="Reference valuation by period">
              <colgroup><col className="w-[21%]" /><col className="w-[7%]" /><col className="w-[13%]" />
                <col className="w-[12%]" /><col className="w-[32%]" /><col className="w-[15%]" /></colgroup>
              <thead className="text-muted"><tr>
                <Th className="whitespace-normal">Delivery period</Th><Th>Hours</Th>
                <Th className="whitespace-normal">Reference EUR/MWh</Th>
                <Th className="whitespace-normal">EUR/RON<br />(RON per EUR)</Th>
                <Th className="whitespace-normal">FX basis / date / source</Th>
                <Th className="whitespace-normal text-right">Estimate EUR</Th>
              </tr></thead>
              <tbody>{components.map((component, index) => (
                <tr key={index} className="border-t border-border/40">
                  <Td className="whitespace-normal">{component.period}</Td>
                  <Td>{component.deliveryHours ?? "—"}</Td>
                  <Td>{component.referenceMarkEUR == null ? "Missing reference mark" : fmtPx(component.referenceMarkEUR, 2)}
                    {valuation.referenceMethod === "exact_contract" && <div className="text-muted">Allocated</div>}
                    {component.marketReferenceMarkEUR != null && <div className="text-muted" title={component.marketReferenceDate ?? undefined}>Market: {fmtPx(component.marketReferenceMarkEUR, 2)}</div>}
                    {component.referenceDate && <div className="text-muted">{component.referenceDate}</div>}</Td>
                  <Td>{component.fxRate == null ? "Missing FX" : fmtPx(component.fxRate, 4)}</Td>
                  <Td className="whitespace-normal break-words">
                    <div>{component.fxKind ?? "—"} · {component.fxDate ?? "—"}</div>
                    <div className="text-muted">{component.fxSource ?? "—"}</div>
                  </Td>
                  <Td className="whitespace-normal text-right">{component.valuationIncomplete
                    ? `Incomplete: ${valuationReasonLabel(component.valuationIncompleteReason)}`
                    : fmtEur0(component.reportingPnlEUR)}</Td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <NativeValueCell
            label="Fixed basis"
            value={valuation.nativeFixedPrice}
            suffix="RON/MWh"
          />
          <NativeValueCell
            label="Native estimate"
            value={valuation.nativePnl}
            suffix="RON"
          />
          <ValueCell label="Reporting estimate" value={data.totalEUR} emphasize />
        </div>
      </section>

      <div className="rounded border border-warnOrange/40 bg-warnOrange/5 px-3 py-2 text-xs font-medium text-fg sm:text-[11px] sm:font-normal sm:text-muted">
        Reference estimate only. This bilateral exposure is excluded from EEX/ECC bank
        reconciliation, variation margin, and Recognition matching; PAIR remains visual.
      </div>
    </Fragment>
  );
}

function NativeValueCell({
  label,
  value,
  suffix,
}: {
  label: string;
  value: number | null;
  suffix: string;
}) {
  return (
    <div className="rounded border border-border/40 px-3 py-2">
      <div className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[11px] sm:font-normal sm:text-muted">{label}</div>
      <div
        className={cn(
          "tabular-nums text-xs font-semibold",
          value != null && value > 0
            ? "text-posGreen"
            : value != null && value < 0
              ? "text-negRed"
              : "text-fg"
        )}
      >
        {value == null ? "—" : `${fmtEur0(value)} ${suffix}`}
      </div>
    </div>
  );
}

function ValueCell({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: number | null;
  emphasize?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded border px-3 py-2",
        emphasize ? "border-accent/50 bg-accent/5" : "border-border/40"
      )}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-fg sm:text-[11px] sm:font-normal sm:text-muted">{label}</div>
      <div
        className={cn(
          "tabular-nums font-semibold",
          emphasize ? "text-sm" : "text-xs",
          value != null && value > 0
            ? "text-posGreen"
            : value != null && value < 0
              ? "text-negRed"
              : "text-fg"
        )}
      >
        {fmtEur0(value)} €
      </div>
    </div>
  );
}
